/**
 * The downsizing module (rebuild, 21 Oct 2026; docs/ux-review/modules/dz/mockup, the village as template).
 *
 * Three steps and a plan page, as the village: (1) the sale and the new home — buy or rent, the price or "test
 * a range", the state and its stamp duty; (2) what it means for them — staying put in four numbers, and the
 * trap in their figures; (3) the ways to use the money — five questions before the first run, then the tested
 * plans on the three levers against staying put, with "Build a plan yourself" (the ledger) one link away.
 * Choosing a plan puts it on record (dzChosenPlan.ts) and the module then opens on its plan page (DzHub), with
 * "Model another home" carrying everything over. The engine and its candidates are unchanged
 * (downsizerScenario.tsx); the advice document (dzSoa.ts) reads the same words and figures.
 */
import { chosenPlanOutOfDate, markRecordOutOfDate, NOT_FOUND_ON_RETEST, profileFingerprint, retestChosenRecord } from '../../utils/chosenPlanRefresh.ts';
import { estateOf } from '../../../../shared/src/calculation/support/estateCgt.ts';
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { withChosenPlan } from '../../strategy/chosenPlan.ts';
import { householdVoice } from '../../utils/householdWords.ts';
import { scrollAppToTop } from '../../utils/scrollAppToTop.ts';
import { MODULE_ICONS } from '../../components/icons/moduleIcons.ts';
import { ChevronLeft, ArrowRight, Home, PiggyBank, TrendingUp, AlertTriangle, Scale, Wallet } from 'lucide-react';
import StrategyRisksPanel from '../../components/strategy/StrategyRisksPanel.tsx';
import { buildDownsizerSoaSnapshot } from './dzSoa.ts';
import { openModuleSoa } from '../advice/moduleSoa.ts';
import { GradientText, FeeExplanationAccordion, Button, IconTile } from '../../components/ui/index.tsx';
import DialogShell from '../../components/ui/DialogShell.tsx';
import { getDownsizeBeforeAfter, runDownsizeProjections, buildDownsizerSuggestions, generateDownsizerCandidates, dzEngineSetupFromGoals, dzReserveOf, isDzSensitivity } from './downsizerScenario.tsx';
import type { Financials, ProjectionYear, DownsizeDetails, DownsizerSuggestionsResult, DzEngineSetup, DzHousing } from './downsizerScenario.tsx';
import { enrichStrategiesWithRecommendationLabels, buildRecommendedOptimalStrategies, mergeGoalLeaderStrategies, type EvaluatedStrategy, type RecommendedStrategyEntry } from '../../strategy/supportAtHomeRecommendedStrategies.ts';
import { candidateReadings } from '../../strategy/planChoiceRule.ts';
import type { GoalFocusId } from '../agedCare/EngineGoalControl.tsx';
import AnalyserSteps, { type AnalyserStep } from '../../components/strategy/AnalyserSteps.tsx';
import { dzSaleFigures, dzDutyFor } from './dzSaleFigures.ts';
import { annuityHorizonFlag, type HorizonFactFind } from '../agedCare/strategyHorizonFlags.ts';
import TransferCapGate from '../../components/TransferCapGate.tsx';
import { tbcQuestionNeededFor } from '../../../../shared/src/calculation/transferBalanceCap.ts';
import { buildNetWorthSchedule } from '../../../../shared/src/calculation/netWorthSchedule.ts';
import { formatCurrency } from '../../utils/index.ts';
import { GENERAL_TRANSFER_BALANCE_CAP } from '../../constants/index.ts';
import DownsizerPlanRecap from './DownsizerPlanRecap.tsx';
import type { DownsizerInsightFinancials } from './downsizerTypes.ts';
import SuggestionEngineAnalysisLoader from '../../components/ui/SuggestionEngineAnalysisLoader.tsx';
import NewMoneyProfileGate from '../../components/NewMoneyProfileGate.tsx';
import { hasAnsweredNewMoneyProfile } from '../../../../shared/src/calculation/investmentProfiles.ts';
import type { InvestmentProfileId } from '../../../../shared/src/calculation/investmentProfiles.ts';
import ProjectionsModal from '../agedCare/ProjectionsModal.tsx';
import { readPeriodMode, type ProjectionsModalPeriodMode } from '../agedCare/projectionsModalPreferences.ts';
import PlanExplanationModal from '../agedCare/PlanExplanationModal.tsx';
import { planRunwayCopy, summariseFunding } from '../../components/insights/fundingSummary.tsx';
import { buildMoneyStory, MoneyStoryBlock } from '../agedCare/moneyStory.tsx';
import DebtPayoffStrategy, { payableDebtsOf } from '../agedCare/DebtPayoffStrategy.tsx';
import { applyDebtPayoffToFactFind, type DebtPayoff } from '../../../../shared/src/calculation/support/debtPayoff.ts';
import * as calculationApi from '../../services/calculationApi.ts';
import { useCalcTracker } from '../../utils/calcTracker.ts';
import { deploymentAnnuityTerms, type ProceedsDeploymentValue } from '../../components/strategy/ProceedsDeploymentFields.tsx';
import { IncomeOnlyBeforeChoose, incomeOnlyHeld, incomeOnlySetter } from '../../components/strategy/IncomeOnlyTick.tsx';
import { EngineAnnuityTerms, AnnuityQuoteControl } from '../../components/strategy/AnnuityTerms.tsx';
import AnnuityFamilyPanel from '../../components/strategy/AnnuityFamilyPanel.tsx';
import { annuityQuoteShapeOf, annuityQuoteNote, type AnnuityQuote } from '../../strategy/annuityQuote.ts';
import { dzGuaranteedIncomeOf, dzInvestSiblingOf, DZ_COMPARE_NOTE } from './dzGuaranteedIncome.ts';
import { buildSoaGuaranteedIncome } from '../advice/soaGuaranteedIncome.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import DzStep1, { dzStateOf, type DzSale } from './DzStep1.tsx';
import DzStep2 from './DzStep2.tsx';
import DzQuestions, { dzAnswersToGoals } from './DzQuestions.tsx';
import DzPlansView, { dzChoiceHeld, type DzSetAside } from './DzPlansView.tsx';
import DzHub from './DzHub.tsx';
import DzAnotherHome from './DzAnotherHome.tsx';
import DzBuildPlan, { type DzLedger } from './DzBuildPlan.tsx';
import DzExplainSteps, { dzExplainIntro } from './DzExplainSteps.tsx';
import { dzLedgerToDetails, dzLedgerFromDetails, dzBlankLedger } from './dzManualPlan.ts';
import { dzPlanWords, dzMoveOfInputs, dzWhereTheMoneyGoes } from './dzPlanWords.ts';
import { dzPlanRisks, dzRiskKindsOf } from './dzPlanRisks.ts';
import { chosenDzPlanOf, dzHomePlansOf, dzHomeKey, withChosenDzPlan, dzPriceWords, DZ_START_FRESH_KEY, DZ_ANOTHER_HOME_KEY, type DzPlanCardRecord, type DzFamilyRecord } from './dzChosenPlan.ts';
import type { RiskKind } from '../../strategy/strategyRisks.ts';
import DebtsInPlanNote, { clearableDebtTotal } from '../../components/strategy/DebtsInPlanNote.tsx';
import HowItIsInvestedNote from '../../components/strategy/HowItIsInvestedNote.tsx';
import { plansInvest } from '../../utils/investedWhere.ts';

interface ExplainSection {
  id: string;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  summary: string;
  details: React.ReactNode;
  /** null = no "May Apply to You" pill: the guide checks nothing about this client (review, 18 Oct 2026). */
  applies: boolean | 'optional' | null;
}

const DZ_EXPLAIN_SECTIONS: ExplainSection[] = [
  { id: 'what', icon: Home, title: 'What downsizing is', summary: 'Selling your home and moving somewhere that suits you better — smaller or easier to run. It does not have to be cheaper.', details: (
    <div className="text-gray-700 leading-relaxed space-y-2">
      <p>Downsizing means selling your current home and moving somewhere that suits you better: smaller, easier to look after, closer to family or services. <strong>It does not have to be cheaper</strong>, and often is not — a low-maintenance apartment in the same suburb can cost as much as the house you are leaving, or more.</p>
      <p><strong>Why people actually move</strong> matters here, because it changes which answer is right:</p>
      <ul className="list-disc pl-5 space-y-1">
        <li><strong>To free up money</strong> for retirement — buy cheaper, and the difference becomes cash you can use.</li>
        <li><strong>To reduce the upkeep</strong> — the garden, the stairs, the maintenance. The money may not change at all.</li>
        <li><strong>To move somewhere better</strong> — closer to family, services or care. That can cost more than the home you sell.</li>
      </ul>
      <p>All three are legitimate, and they lead to different financial outcomes. The next two sections are the two directions this can go — and they are opposites.</p>
    </div>
  ), applies: null },
  { id: 'trap', icon: AlertTriangle, title: 'Buying CHEAPER: the Age Pension "downsizing trap"', summary: 'Your home is exempt from the assets test whatever it is worth, but the cash you free up is counted — so buying cheaper can reduce your pension.', details: (
    <div className="text-gray-700 leading-relaxed space-y-2">
      <p>Your home doesn&apos;t count towards the Age Pension assets test, <strong>no matter its value</strong>. When you sell and buy something cheaper, the money you free up <strong>becomes an assessable asset</strong> — and is deemed to earn income on top. That can reduce your Age Pension, sometimes to nil.</p>
      <p>It can still be the right move: freed-up cash is money you can actually use, and a pension you lose is not the only thing that matters. But you should see the size of the effect before deciding, which is what the calculator is for.</p>
    </div>
  ), applies: null },
  /*
   * THE OPPOSITE OF THE TRAP, and the module never said it (owner, 2026-08-28).
   *
   * Every word of this explainer assumed downsizing means buying cheaper, so the only pension story
   * it could tell was the trap. But the exemption that CAUSES the trap runs both ways: the home is
   * exempt at any value, so moving counted money INTO a dearer home lifts the pension by the same
   * mechanism that buying cheaper lowers it.
   *
   * Measured against the engine on a $1m homeowner with $500k in savings, a $1.15m purchase took the
   * pension from $17,300 to $29,418 — see server/.../downsizerNewHomePrice.test.ts. That is a real
   * strategy an adviser would consider, and it is exactly the person who moves for MAINTENANCE
   * rather than money who is most likely to be buying at or above what they sell for.
   *
   * It is stated with its costs, not as a trick. The money is in the house, it is not deprivation,
   * and it only helps someone the assets test is actually biting.
   */
  { id: 'buyup', icon: TrendingUp, title: 'Buying SMALLER BUT DEARER: the opposite of the trap', summary: 'The home is exempt at any value — so moving money into a more expensive home can INCREASE your Age Pension.', details: (
    <div className="text-gray-700 leading-relaxed space-y-2">
      <p>The rule that causes the trap works both ways. If the home is exempt <strong>whatever it is worth</strong>, then money moved out of your savings and into a dearer home <strong>stops being counted</strong> — and the Age Pension can go <strong>up</strong>.</p>
      <p>That is not a loophole, and it is worth being clear about why. If you <strong>give money away</strong>, Centrelink does not simply stop counting it — anything above $10,000 in a year (or $30,000 over five years) is treated as though you still own it <strong>for the next five years</strong>. That rule is called <em>deprivation</em>, and it exists to stop people giving assets away to qualify for a pension.</p>
      <p><strong>Buying a home is not giving money away.</strong> You still own every dollar — it is simply held in a house instead of a bank account. So nothing is treated as deprived, and that five-year rule does not apply here at all. It is a recognised strategy, and it suits the person who is moving for the upkeep rather than the money: a smaller, newer, easier place that happens to cost more than the house they are leaving.</p>
      <p><strong>What it costs you, and this is the real trade-off:</strong></p>
      <ul className="list-disc pl-5 space-y-1">
        <li><strong>The money is in the house.</strong> You cannot spend it, and getting it back means selling again or borrowing against it.</li>
        <li><strong>It only helps if the assets test is what is limiting your pension.</strong> On a full pension already, or limited by the income test, there may be nothing to gain.</li>
        <li><strong>Aged care assesses the home differently.</strong> If residential care is on the horizon, a larger home is counted there up to a cap — so this can help the pension and cost you later.</li>
        <li><strong>Buying and selling costs are real</strong> and are paid whichever direction you move.</li>
      </ul>
      <p>The Suggestion Engine models a cheaper, a similar-priced and a dearer move side by side, so you can see which way your own numbers fall rather than assuming.</p>
    </div>
  ), applies: null },
  { id: 'exemption', icon: Scale, title: 'The 24-month proceeds exemption', summary: 'Proceeds you intend to use for a new home are exempt from the assets test for up to 24 months (longer if delayed), and deemed at the lower rate.', details: <p className="text-gray-700 leading-relaxed">If you sell before you&apos;ve bought or built your new home, the portion of the proceeds you <strong>intend to use for the new home</strong> is exempt from the assets test for up to <strong>24 months</strong> (and up to 36 months if you&apos;re delayed for reasons outside your control). During that time it&apos;s counted under the income test at the <strong>lower deeming rate</strong> only. Once you buy, the new home is exempt. Tell Services Australia within 14 days of selling.</p>, applies: null },
  { id: 'super', icon: PiggyBank, title: 'Topping up super (downsizer contribution)', summary: 'If you are 55+, you can put up to $300,000 each from the sale into super — outside the normal caps, but with some important rules.', details: (
    <div className="text-gray-700 leading-relaxed space-y-2">
      <p>If you&apos;re <strong>55 or older</strong> and you (or your spouse) owned the home for <strong>10+ years</strong>, you can contribute up to <strong>$300,000 each</strong> ($600,000 for a couple) from the sale into super as a <strong>downsizer contribution</strong>, within <strong>90 days</strong> of settlement. No work test, no upper age limit, and you don&apos;t have to actually buy a new (or cheaper) home.</p>
      <p><strong>The rules worth knowing:</strong></p>
      <ul className="list-disc pl-5 space-y-1">
        <li><strong>Once only.</strong> You can use the downsizer contribution just once, ever, from a single home sale.</li>
        <li><strong>Doesn&apos;t use your contribution caps.</strong> It&apos;s on top of your normal concessional/non-concessional caps, and you can make it even if your total super balance is already large.</li>
        <li><strong>But the transfer balance cap still applies.</strong> Money held in a <em>retirement pension</em> account inside super pays <strong>no tax at all</strong> on its earnings — so there is a lifetime limit on how much you are allowed to move into one. That limit is the <strong>transfer balance cap</strong>, currently <strong>{formatCurrency(GENERAL_TRANSFER_BALANCE_CAP)}</strong>, and a downsizer contribution counts towards it like any other money. Anything above the cap has to stay in an <em>accumulation</em> account instead, where earnings are taxed at 15% — still low, just not free.</li>
        <li><strong>Very large balances:</strong> from 1 July 2026 an extra 15% tax applies to earnings on the part of any super balance over $3 million (Division 296).</li>
      </ul>
      <p><strong>Why bother?</strong> Inside super, investment earnings are taxed at most 15% (and 0% in pension phase) — usually far less than in your own name. The trade-off: once you&apos;re Age-Pension age, super counts in the means test, so moving home equity (exempt) into super (assessable) can reduce your pension. The Suggestion Engine weighs that tax saving against the pension cost on your own figures, so you can see which way it lands rather than guessing.</p>
    </div>
  ), applies: null },
  { id: 'cgt', icon: Wallet, title: 'Capital Gains Tax', summary: 'Selling your main residence is usually CGT-free.', details: <p className="text-gray-700 leading-relaxed">Your main residence is generally <strong>exempt from Capital Gains Tax</strong>. If part of the home was used to produce income (e.g. rented out or a home business), a partial CGT liability can apply. Confirm with your accountant.</p>, applies: null },
];

interface DownsizerExplainedProps {
  setActivePage: (page: string) => void;
  onContinue: () => void;
  /** Set when opened from inside the module: both buttons say it and go back there. */
  backLabel?: string;
}

const DownsizerExplained = ({ setActivePage, onContinue, backLabel }: DownsizerExplainedProps) => {
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);
  const toggle = (id: string) => setOpenAccordion(openAccordion === id ? null : id);
  return (
    <div className="max-w-6xl mx-auto">
      {/* Back to where the reader was, top and bottom, as aged care's and the village's guides (owner, 22 Oct 2026). */}
      <Button variant="ghost" size="sm" icon={ChevronLeft} onClick={() => (backLabel ? onContinue() : setActivePage('Dashboard'))} className="mb-4 -ml-2" data-testid="dz-guide-back">{backLabel || 'Back to Dashboard'}</Button>
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-4">
          <IconTile icon={MODULE_ICONS.downsizer} tone="brand" variant="solid" size="lg" className="hidden sm:inline-flex" />
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900"><GradientText>Downsizing Your Home</GradientText></h1>
        </div>
        <p className="text-lg text-slate-700 leading-relaxed mb-3">Selling the family home and moving somewhere that suits you better — smaller, easier to run, closer to family. It might free up money, or it might not; either way it changes your Age Pension and where your wealth sits. Let&apos;s model it so there are no surprises.</p>
        <p className="text-sm text-slate-500">General information and modelling only. You can confirm pension impacts free with Services Australia&apos;s Financial Information Service, and your accountant should look at any capital gains tax before you sell.</p>
      </div>
      <div className="space-y-4 mb-8">
        {DZ_EXPLAIN_SECTIONS.map((fee) => (
          <FeeExplanationAccordion key={fee.id} {...fee} isOpen={openAccordion === fee.id} onToggle={() => toggle(fee.id)} factFind={{}} />
        ))}
      </div>
      <div className="text-center">
        <Button onClick={onContinue} variant="brand" size="lg" iconRight={ArrowRight}>{backLabel || 'Continue to calculator'}</Button>
      </div>
    </div>
  );
};

/** A tested plan from the engine — EvaluatedStrategy plus the fields this module reads off it. */
export interface DzStrategyView extends EvaluatedStrategy {
  id: string;
  description?: string;
  inputs?: DownsizeDetails;
  projections?: ProjectionYear[];
  deploymentBreakdown?: Array<{ label: string; amount: number }>;
  /** Weekly rent the plan pays where it rents instead of buying (the evaluator's figure). */
  rentPerWeek?: number;
  /** A price what-if: listed and explained, never a goal leader. */
  sensitivity?: boolean;
}

interface SeView {
  /** The engine card this explanation is of — a view of another card is never shown for this one. */
  id?: string;
  name?: string;
  before: Financials;
  after: Financials;
  projections: ProjectionYear[];
  hasDownsize: boolean;
  salePrice: number;
  sellingCosts: number;
  newHomePrice: number;
  stampDuty?: number;
  freedCapital: number;
  buyUpShortfall: number;
  sellAndRent: boolean;
  rentPerWeek: number;
  downsizerContribution: number;
  legacyEstimate: number;
  insightFinancials: DownsizerInsightFinancials;
}

interface DownsizerAnalyserProps {
  setActivePage: (page: string) => void;
  factFind: DraftFactFind;
  onBack: () => void;
  /** The file's saved plans — the chosen downsizing plan and every home's plan are read from them. */
  savedStrategies?: Array<Record<string, unknown>>;
  setSavedStrategies?: React.Dispatch<React.SetStateAction<Array<Record<string, unknown>>>>;
  /** Persists the answers to the client record (App owns the fact-find state). */
  setFactFind?: React.Dispatch<React.SetStateAction<DraftFactFind>>;
  onSoaGenerated?: (document: Record<string, unknown>) => void;
}

const n = (v: unknown) => Number(v) || 0;

/** The age-and-health warning for a candidate's annuity — the aged care rule, applied here. */
function dzAnnuityHorizonWarningOf(strategy: { inputs?: DownsizeDetails | null }, factFind: DraftFactFind): string | null {
  const terms = deploymentAnnuityTerms(strategy.inputs?.deployment as ProceedsDeploymentValue | undefined, factFind?.adviceFor === 'couple');
  if (!terms) return null;
  const flag = annuityHorizonFlag({ factFind: factFind as unknown as HorizonFactFind, residualCapital: (Number(terms.residualPercent) || 0) / 100, owner: terms.owner });
  return flag?.warning ?? null;
}

const DownsizerAnalyser = ({ setActivePage, factFind, onBack, savedStrategies = [], setSavedStrategies, setFactFind, onSoaGenerated }: DownsizerAnalyserProps) => {
  const isCouple = factFind?.adviceFor === 'couple';
  const homeValue = parseFloat(String(factFind?.home?.value ?? '')) || 0;
  const hasHome = homeValue > 0;
  /** "you" to the client, the household's names to an adviser (owner, 21 Oct 2026). */
  const voice = householdVoice(factFind);
  const goals = (factFind?.goals || {}) as Record<string, unknown>;
  const setGoal = (patch: Record<string, unknown>) => setFactFind?.((prev) => ({ ...prev, goals: { ...(prev?.goals || {}), ...patch } }));

  /*
   * HOW THE MODULE OPENS (as the village, 21 Oct 2026). With a plan chosen, on the plan page (DzHub). From
   * Home's "Start a new model", on step 1 with step 3's questions asked again. From "Model another home", on
   * the another-home step with everything carried over. The session flags are read here and cleared below.
   */
  const [openedFresh] = useState<boolean>(() => { try { return !!sessionStorage.getItem(DZ_START_FRESH_KEY); } catch { return false; } });
  const [anotherHome, setAnotherHome] = useState<boolean>(() => { try { return !!sessionStorage.getItem(DZ_ANOTHER_HOME_KEY); } catch { return false; } });
  const dzChosen = useMemo(() => chosenDzPlanOf(savedStrategies), [savedStrategies]);
  const dzHomePlans = useMemo(() => dzHomePlansOf(savedStrategies), [savedStrategies]);
  const [dzView, setDzView] = useState<'hub' | 'steps'>(() => (!openedFresh && !anotherHome && chosenDzPlanOf(savedStrategies) ? 'hub' : 'steps'));
  const [pendingHubAction, setPendingHubAction] = useState<'explain' | 'advice' | null>(null);
  // Step 3 asks its questions before the first run, and again from "Things that change the answer". The
  // emergency money is the rebuild's own question, so a file that has it has answered them.
  const [dzAsking, setDzAsking] = useState<boolean>(() => openedFresh || (factFind?.goals as Record<string, unknown> | undefined)?.dzEmergencyReserve == null);
  useEffect(() => {
    try { sessionStorage.removeItem(DZ_START_FRESH_KEY); sessionStorage.removeItem(DZ_ANOTHER_HOME_KEY); } catch { /* storage unavailable */ }
    if (openedFresh && setFactFind) {
      // A fresh model: the home, the price and the answers taken off the file so nothing carries over.
      setFactFind((prev) => {
        const g = { ...(prev?.goals || {}) } as Record<string, unknown>;
        for (const k of ['dzNewHomePrice', 'dzHomeName', 'dzRentPerWeek', 'dzEmergencyReserve', 'dzDownsizeDate', 'dzSalePrice', 'dzSellingCosts']) delete g[k];
        return { ...prev, goals: g };
      });
    }
    // From Home's "Model another home": the chosen plan's sale carried over, the home itself left to name and price.
    if (anotherHome && !openedFresh && setFactFind) {
      const m = chosenDzPlanOf(savedStrategies)?.inputs?.card?.move;
      setFactFind((prev) => ({ ...prev, goals: { ...(prev?.goals || {}), ...(m ? { dzSalePrice: m.salePrice, dzSellingCosts: m.sellingCosts, dzDownsizeDate: m.date || '', dzNewHomeState: m.state } : {}), dzHomeName: '', dzHousing: 'buy' } }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * THE THREE STEPS. 1: the sale and the new home. 2: staying put, the one baseline. 3: the ways to use the
   * money — the tested plans, or "Build a plan yourself". The strip is a map, not a gate.
   */
  const [step, setStep] = useState<AnalyserStep>(1);
  const [strategyMode, setStrategyMode] = useState<'optimised' | 'manual'>('optimised');
  const goToStep = (next: AnalyserStep) => { setStep(next); scrollAppToTop(); };

  // ── Step 1's answers, kept on the file (goals.dz*) ──
  const sale: DzSale = useMemo(() => {
    const salePrice = goals.dzSalePrice != null && n(goals.dzSalePrice) > 0 ? n(goals.dzSalePrice) : homeValue;
    return {
      salePrice,
      sellingCosts: goals.dzSellingCosts != null ? n(goals.dzSellingCosts) : Math.round(salePrice * 0.025),
      date: typeof goals.dzDownsizeDate === 'string' ? goals.dzDownsizeDate : '',
    };
  }, [goals.dzSalePrice, goals.dzSellingCosts, goals.dzDownsizeDate, homeValue]);
  const onSale = (patch: Partial<DzSale>) => setGoal({
    // Until the selling costs are typed they follow the price at 2.5% (the `sale` reading above).
    ...(patch.salePrice != null ? { dzSalePrice: patch.salePrice } : {}),
    ...(patch.sellingCosts != null ? { dzSellingCosts: patch.sellingCosts } : {}),
    ...(patch.date != null ? { dzDownsizeDate: patch.date } : {}),
  });
  const housing: DzHousing = goals.dzHousing === 'rent' || goals.dzHousing === 'both' ? goals.dzHousing : 'buy';
  const namedPrice = n(goals.dzNewHomePrice);
  const homeName = String(goals.dzHomeName || '').trim();
  const dutyState = dzStateOf(factFind).code;
  const rentAWeek = n(goals.dzRentPerWeek);
  const downsizeDate = sale.date;
  const downsizeDateInPast = downsizeDate !== '' && downsizeDate < new Date().toISOString().slice(0, 10);
  const effectiveDownsizeDate = downsizeDateInPast ? '' : downsizeDate;
  // 10 years, the same as every module but aged care (owner, 19 Oct 2026); the client's answer where given.
  const localLegacyYears = Math.min(30, Math.max(1, n(goals.dzLegacyYears) || 10));
  const setLocalLegacyYears = (y: number) => setGoal({ dzLegacyYears: Math.min(30, Math.max(1, Math.round(y) || 10)) });

  /*
   * THE ENGINE'S SETUP, from the file: step 1's home, price, rent and state (the state the client chose, even
   * "not sure"), and step 3's answers. One run on the answers, never on defaults first.
   */
  const dzSetup: DzEngineSetup = useMemo(() => ({ ...dzEngineSetupFromGoals(factFind), newHomeState: dutyState }), [factFind, dutyState]);
  const reserve = dzReserveOf(factFind, dzSetup);

  // ── Build a plan yourself: the ledger (Manual Control's state), seeded from step 1 ──
  const ledgerFromStep1 = (): DzLedger => {
    const price = namedPrice > 0 ? namedPrice : Math.round(sale.salePrice * 0.7);
    return {
      salePrice: sale.salePrice, sellingCosts: sale.sellingCosts, sellAndRent: housing === 'rent', rentPerWeek: rentAWeek,
      newHomePurchasePrice: price, stampDuty: dzDutyFor(dutyState, price), newHomeState: dutyState,
      downsizerContribution: 0, partnerDownsizerContribution: 0, commenceIncomeStream: false, giftingAmount: 0, funeralBondPurchase: 0,
      deployment: {}, buyUpFunding: {}, buyUpCgtOverrides: {},
    };
  };
  const [ledger, setLedger] = useState<DzLedger>(ledgerFromStep1);
  /*
   * Whether a plan was actually built by hand. Until then the ledger is nobody's plan, so the engine does not
   * list it as "Your current plan" (review, 18 Oct 2026), and it follows step 1.
   */
  const [manualPlanEntered, setManualPlanEntered] = useState(false);
  const [manualStartedFrom, setManualStartedFrom] = useState<string | null>(null);
  const [manualLastChange, setManualLastChange] = useState<string | null>(null);
  const [manualChosen, setManualChosen] = useState(false);
  const onLedger = (patch: Partial<DzLedger>, words?: string) => {
    setLedger((prev) => {
      const next = { ...prev, ...patch };
      // A new price or state strikes the duty again (StampDutyFields does the same on screen).
      if (('newHomePurchasePrice' in patch || 'newHomeState' in patch) && !('stampDuty' in patch)) next.stampDuty = dzDutyFor(next.newHomeState, next.newHomePurchasePrice);
      return next;
    });
    if (words) { setManualLastChange(words); setManualPlanEntered(true); }
    setManualChosen(false);
  };
  // Step 1 is the sale for both ways in; the home, price and state follow it until a plan is built by hand.
  useEffect(() => {
    setLedger((prev) => ({ ...prev, salePrice: sale.salePrice, sellingCosts: sale.sellingCosts, ...(manualPlanEntered ? {} : (() => { const s = ledgerFromStep1(); return { sellAndRent: s.sellAndRent, rentPerWeek: s.rentPerWeek, newHomePurchasePrice: s.newHomePurchasePrice, stampDuty: s.stampDuty, newHomeState: s.newHomeState }; })()) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sale.salePrice, sale.sellingCosts, housing, namedPrice, dutyState, rentAWeek]);
  const details = useMemo<DownsizeDetails>(() => dzLedgerToDetails(ledger), [ledger]);
  const hasDownsize = hasHome && sale.salePrice > 0;

  /** The shared debt strategy — the hand-built plan's; the engine pairs a debt twin with every plan. */
  const [debtPayments, setDebtPayments] = useState<Record<string, number>>({});
  const [debtFunding, setDebtFunding] = useState<Record<string, number>>({});
  const [debtRepaymentBasis, setDebtRepaymentBasis] = useState<'keep_repayment' | 'lower_repayment'>('keep_repayment');
  const debtPayoffPlan = useMemo<DebtPayoff | null>(() => {
    const payments = Object.fromEntries(Object.entries(debtPayments).filter(([, amount]) => (Number(amount) || 0) > 0));
    if (Object.keys(payments).length === 0) return null;
    const funding = Object.fromEntries(Object.entries(debtFunding).filter(([, amount]) => (Number(amount) || 0) > 0));
    return { payments, funding, repaymentBasis: debtRepaymentBasis };
  }, [debtPayments, debtFunding, debtRepaymentBasis]);

  /* TODAY'S POSITION — step 2's four numbers and the baseline of every "why?". */
  const [todayFinancials, setTodayFinancials] = useState<Financials | null>(null);
  useEffect(() => {
    let cancelled = false;
    calculationApi.calculateFinancials(factFind).then((r) => { if (!cancelled) setTodayFinancials(r as Financials); }).catch(() => { if (!cancelled) setTodayFinancials(null); });
    return () => { cancelled = true; };
  }, [factFind]);

  /* THE BENCHMARK: the client's position over the horizon if they simply do not downsize — the same run the engine's stay-put candidate makes. */
  /** The hand-built plan's figures are still being worked out (utils/calcTracker.ts, 25 Oct 2026). */
  const { track: trackDz, working: dzCalcWorking } = useCalcTracker();
  const [stayPutProjections, setStayPutProjections] = useState<ProjectionYear[]>([]);
  useEffect(() => {
    let cancelled = false;
    trackDz(runDownsizeProjections(factFind, { legacyYears: localLegacyYears }))
      .then((rows) => { if (!cancelled) setStayPutProjections(rows || []); })
      .catch(() => { if (!cancelled) setStayPutProjections([]); });
    return () => { cancelled = true; };
  }, [factFind, localLegacyYears]);
  const manualBaselineLegacy: number | null = useMemo(() => estateOf(stayPutProjections[localLegacyYears - 1] ?? stayPutProjections[stayPutProjections.length - 1]), [stayPutProjections, localLegacyYears]);
  const stayPutRunway = useMemo(() => (stayPutProjections.length ? planRunwayCopy(stayPutProjections as never, localLegacyYears) : null), [stayPutProjections, localLegacyYears]);

  /* THE HAND-BUILT PLAN'S FIGURES — only while it is being built (the engine's rows carry every tested plan's). */
  const building = strategyMode === 'manual' && step === 3 && dzView === 'steps';
  const [{ before, after: afterRaw }, setBeforeAfter] = useState<{ before: Financials; after: Financials }>({ before: {}, after: {} });
  const [afterWithDebt, setAfterWithDebt] = useState<Financials | null>(null);
  const [projections, setProjections] = useState<ProjectionYear[]>([]);
  useEffect(() => {
    if (!building || !hasDownsize) return undefined;
    let cancelled = false;
    void trackDz((async () => {
      try {
        const r = await getDownsizeBeforeAfter(factFind, details);
        const { simulated } = r;
        if (cancelled) return;
        setBeforeAfter({ before: r.before, after: r.after });
        if (debtPayoffPlan) {
          const withDebt = applyDebtPayoffToFactFind(simulated as never, debtPayoffPlan).factFind;
          const fin = (await calculationApi.calculateFinancials(withDebt)) as Financials;
          if (!cancelled) setAfterWithDebt(fin);
        } else setAfterWithDebt(null);
        const proj = effectiveDownsizeDate
          // Dated: the ORIGINAL fact-find goes to the engine, which sells, buys and allocates on the day.
          ? await runDownsizeProjections(factFind, { legacyYears: localLegacyYears, downsizeDate: effectiveDownsizeDate, downsizeDetails: details as never, debtPayoff: debtPayoffPlan as never })
          : await runDownsizeProjections(debtPayoffPlan ? (applyDebtPayoffToFactFind(simulated as never, debtPayoffPlan).factFind as never) : simulated, { legacyYears: localLegacyYears });
        if (!cancelled) setProjections(proj);
      } catch { if (!cancelled) setProjections([]); }
    })());
    return () => { cancelled = true; };
  }, [building, factFind, details, hasDownsize, localLegacyYears, debtPayoffPlan, effectiveDownsizeDate]);
  const after = afterWithDebt ?? afterRaw;
  const manualLegacy = projections.length > 0 ? (estateOf(projections[localLegacyYears - 1] ?? projections[projections.length - 1]) ?? n(after?.netWorth)) : null;

  // The just-in-time gates the questions do not cover: how new money is invested (when the questions were
  // skipped), and the transfer balance cap when a run would start a pension and a balance trips the screen.
  const [newMoneySkipped, setNewMoneySkipped] = useState(false);
  // The module's own questions ask this; "Not sure" there is an answer that records nothing, so once they are answered the
  // old one-question gate never shows (owner, 22 Oct 2026: it stood alone in place of the questions on a return visit).
  const needsNewMoneyAnswer = strategyMode === 'optimised' && hasDownsize && !hasAnsweredNewMoneyProfile(factFind) && !newMoneySkipped && (factFind?.goals as Record<string, unknown> | undefined)?.dzEmergencyReserve == null;
  const answerNewMoneyProfile = (id: InvestmentProfileId) => {
    if (setFactFind) setFactFind((prev) => ({ ...prev, goals: { ...(prev.goals || {}), newMoneyInvestmentProfile: id } }));
    else setNewMoneySkipped(true);
  };
  const [tbcSkipped, setTbcSkipped] = useState(false);
  const seRunCommencesPension = useMemo(() => {
    if (!hasDownsize) return false;
    try {
      return generateDownsizerCandidates(factFind, { currentInputs: details, manualPlanEntered, setup: dzSetup }).some((c) => {
        const d = c.details as { commenceIncomeStream?: boolean; deployment?: { superCommenceIncomeStream?: boolean } };
        return d.commenceIncomeStream === true || d.deployment?.superCommenceIncomeStream === true;
      });
    } catch { return false; }
  }, [factFind, details, manualPlanEntered, dzSetup, hasDownsize]);
  const tbcPersonNeedingAnswer: 'client' | 'partner' | null = !tbcSkipped && seRunCommencesPension
    ? (tbcQuestionNeededFor(factFind as never, 'client') ? 'client' : (isCouple && tbcQuestionNeededFor(factFind as never, 'partner') ? 'partner' : null))
    : null;
  const needsTbcAnswer = strategyMode === 'optimised' && tbcPersonNeedingAnswer !== null;
  const answerTbc = (amount: number) => {
    if (setFactFind && tbcPersonNeedingAnswer) {
      const key = tbcPersonNeedingAnswer;
      setFactFind((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), transferBalanceUsed: amount } }));
    } else setTbcSkipped(true);
  };

  /*
   * A real provider quote for the annuity plans (annuityQuote.ts): it belongs to one type and amount; plans of
   * the same type use it scaled to theirs. Only the committed value re-runs the field.
   */
  const [dzAnnuityQuote, setDzAnnuityQuote] = useState<AnnuityQuote | null>(null);
  const [dzAnnuityQuoteDraft, setDzAnnuityQuoteDraft] = useState<number | string>('');

  // The engine: paint the loader, then compute, then hold to a minimum (the shared pattern).
  const [seLoading, setSeLoading] = useState(false);
  const [dzSuggestions, setDzSuggestions] = useState<DownsizerSuggestionsResult | null>(null);
  const engineWanted = strategyMode === 'optimised' && ((dzView === 'steps' && step === 3) || pendingHubAction != null);
  useEffect(() => {
    if (!engineWanted) return undefined;
    if (dzAsking || anotherHome) { setSeLoading(false); return undefined; }
    if (needsNewMoneyAnswer || needsTbcAnswer) { setSeLoading(false); return undefined; }
    if (!hasDownsize) { setDzSuggestions(null); setSeLoading(false); return undefined; }
    setSeLoading(true);
    const startedAt = Date.now();
    const t = setTimeout(async () => {
      let result: DownsizerSuggestionsResult | null = null;
      try { result = await buildDownsizerSuggestions(factFind, { legacyYears: localLegacyYears, currentInputs: details, manualPlanEntered, downsizeDate: effectiveDownsizeDate || undefined, setup: dzSetup, annuityQuote: dzAnnuityQuote }); } catch { result = null; }
      // A failed run is said, not left spinning: an empty field shows "we couldn't test the plans".
      setDzSuggestions(result ?? { strategies: [], recommended: [], stayPut: null, rerun: { candidates: [], legacyYears: localLegacyYears } });
      setTimeout(() => setSeLoading(false), Math.max(0, 1500 - (Date.now() - startedAt)));
    }, 60);
    return () => clearTimeout(t);
  }, [engineWanted, factFind, localLegacyYears, details, manualPlanEntered, hasDownsize, needsNewMoneyAnswer, needsTbcAnswer, dzSetup, dzAsking, anotherHome, effectiveDownsizeDate, dzAnnuityQuote]);

  const dzStrategies = useMemo(() => (dzSuggestions ? enrichStrategiesWithRecommendationLabels(dzSuggestions.strategies, dzSuggestions.recommended) : []) as DzStrategyView[], [dzSuggestions]);
  const dzStayPut = useMemo(() => (dzSuggestions?.stayPut as DzStrategyView | null | undefined) ?? null, [dzSuggestions]);
  const isDzBaseline = (s: DzStrategyView) => s.id === 'dz-stay-put';
  /*
   * A NAMED HOME (journey 2): the cards are the ways to use the money for THAT home — "Leaves the most, for
   * this home" — and the other prices stay in "every way tested" as what-ifs. Otherwise the client's reason
   * for moving decides which prices compete, as before.
   */
  const forHome = homeName && namedPrice > 0 && housing === 'buy' ? homeName : null;
  // A price stated on step 1 — named home or not — makes the other prices what-ifs (25 Oct 2026; the generator
  // flags them too, this also covers a run made before that).
  const isWhatIf = (s: DzStrategyView) => isDzSensitivity(s) || (namedPrice > 0 && housing !== 'rent' && /^dz-price-/.test(String(s.id)));
  /*
   * GOAL LEADERS, with the aged care horizon rule (a flagged strategy does not win a ranking; it is said and
   * can be opened) and the named-home rule above. Ranked among the ways of downsizing, against staying put.
   */
  const dzRecommended = useMemo<RecommendedStrategyEntry[]>(() => {
    if (!dzSuggestions) return [];
    const movers = dzStrategies.filter((s) => !isDzBaseline(s) && !isWhatIf(s));
    const eligible = movers.filter((s) => dzAnnuityHorizonWarningOf(s, factFind) == null);
    /*
     * RANKED AMONG THE WAYS OF DOWNSIZING, not against staying put (the village's rule, owner 20–21 Oct 2026; found in the
     * live review 22 Oct 2026: when every way left less than staying put, the shared ranking withheld them all and
     * the plans screen showed no cards). The price is said in one line above the cards and an "Important"
     * line on each, against staying put.
     */
    return buildRecommendedOptimalStrategies(eligible, null, { cashflowTarget: Number(dzSetup.cashflowTarget) || 0, readings: candidateReadings });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dzSuggestions, dzStrategies, dzStayPut, factFind, dzSetup.cashflowTarget, forHome]);
  const dzGoalLeaders: DzStrategyView[] = useMemo(() => mergeGoalLeaderStrategies(dzRecommended) as DzStrategyView[], [dzRecommended]);
  const dzSetAside = useMemo<DzSetAside[]>(() => {
    if (!dzSuggestions) return [];
    return (mergeGoalLeaderStrategies(dzSuggestions.recommended) as DzStrategyView[])
      .map((s) => ({ strategy: s, labels: s.recommendationLabels || [], warning: dzAnnuityHorizonWarningOf(s, factFind) || '' }))
      .filter((x) => x.warning);
  }, [dzSuggestions, factFind]);
  const [optimisationFocus, setOptimisationFocus] = useState<GoalFocusId>('legacy');

  /** The tested plan chosen on an earlier visit, for its card. */
  const dzChosenEngineId = useMemo(() => {
    const id = dzChosen?.source === 'suggestion_engine' ? dzChosen.inputs?.engineStrategyId : null;
    return typeof id === 'string' ? id : null;
  }, [dzChosen]);
  const [selectedSeId, setSelectedSeId] = useState<string | null>(null);
  const currentHomeKey = dzHomeKey(homeName, namedPrice, housing);
  /*
   * THE CHOSEN PLAN'S FIGURES FOLLOW ITS RE-TEST (owner, 25 Oct 2026: as aged care and Support at home). Tested
   * again for the chosen home over the same years, the chosen tested plan is brought up to date on its record (Home
   * and the plan page read it), or marked out of date where the run no longer has it.
   */
  useEffect(() => {
    if (!dzChosen || dzChosen.source !== 'suggestion_engine' || !dzSuggestions || seLoading || !setSavedStrategies) return;
    const ins = (dzChosen.inputs || {}) as { homeKey?: string; engineStrategyId?: unknown; card?: { horizonYears?: number } };
    if (ins.homeKey !== currentHomeKey || Number(ins.card?.horizonYears) !== Number(localLegacyYears)) return;
    const row = dzStrategies.find((x) => x.id === ins.engineStrategyId);
    const base = dzStrategies.find((x) => x.id === 'dz-stay-put')?.snapshot;
    const chosenId = (dzChosen as { id?: unknown }).id;
    setSavedStrategies((prev) => {
      const next = row
        ? retestChosenRecord(prev as never, chosenId, { legacy: n(row.snapshot?.legacyValue), pension: n(row.snapshot?.pension), cashflow: n(row.snapshot?.netCashflow) }, base ? { legacy: n(base.legacyValue), pension: n(base.pension), cashflow: n(base.netCashflow) } : null, profileFingerprint(factFind))
        : markRecordOutOfDate(prev as never, chosenId, NOT_FOUND_ON_RETEST);
      return (next as typeof prev | null) ?? prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dzSuggestions, seLoading]);
  /** Whether the chosen plan is for the home on screen (set below, read when choosing). */
  const chosenHereRef = React.useRef(false);

  /** What the family receives from a plan's guaranteed income, frozen for the plan page (as the village). */
  const familyRecordOf = (inputs: DownsizeDetails | undefined, rows: ProjectionYear[] | undefined, sibling: DzStrategyView | null): DzFamilyRecord | null => {
    const gi = dzGuaranteedIncomeOf(inputs?.deployment as ProceedsDeploymentValue | undefined, factFind, rows);
    if (!gi || gi.annuityType === 'fixedTerm') return null;
    const built = buildSoaGuaranteedIncome({ factFind, personName: gi.personName, amount: gi.amount, kind: gi.kind, incomeAYear: gi.incomeAYear, rows: rows as unknown[], compareRows: (sibling?.projections ?? null) as unknown[] | null, compareNote: sibling ? DZ_COMPARE_NOTE : null, horizonYears: localLegacyYears, plan: { estate: 0, pension: 0, cashflow: 0 } });
    return built ? { personName: gi.personName, amount: gi.amount, kind: gi.kind, incomeAYear: gi.incomeAYear, compareNote: built.compareNote, rows: built.rows } : null;
  };

  /*
   * CHOOSING A TESTED PLAN puts it on record with its card (dzChosenPlan.ts): one plan per home; another home's
   * plan stays saved. Income only waits for the client's dated confirmation for this amount.
   */
  const selectEngineStrategy = (strategy: DzStrategyView) => {
    if (dzChoiceHeld(strategy, factFind)) return;
    // Already chosen (here, or on an earlier visit for this home): nothing to save again.
    if (strategy.id != null && strategy.id === (selectedSeId ?? (chosenHereRef.current ? dzChosenEngineId : null))) return;
    setSelectedSeId(strategy.id ?? null);
    if (!setSavedStrategies) return;
    const recommended = dzGoalLeaders.map((l) => ({ labels: l.recommendationLabels || [], name: l.name }));
    const baseSnap = dzStayPut?.snapshot;
    const move = { ...dzMoveOfInputs(strategy.inputs as Record<string, unknown>, { rentPerWeek: strategy.rentPerWeek, homeName, date: effectiveDownsizeDate }) };
    const card: DzPlanCardRecord = {
      plan: { legacy: n(strategy.snapshot?.legacyValue), pension: n(strategy.snapshot?.pension), cashflow: n(strategy.snapshot?.netCashflow) },
      baseline: baseSnap ? { legacy: n(baseSnap.legacyValue), pension: n(baseSnap.pension), cashflow: n(baseSnap.netCashflow) } : null,
      words: dzPlanWords(strategy, factFind),
      move,
      horizonYears: localLegacyYears,
      riskKinds: dzRiskKindsOf(String(strategy.id || ''), strategy.inputs as Record<string, unknown>),
      origin: 'engine',
      family: familyRecordOf(strategy.inputs, strategy.projections, dzInvestSiblingOf(strategy.id, dzStrategies)),
    };
    setSavedStrategies((prev) => withChosenDzPlan(prev as never, {
      id: `dz-se-${Date.now()}`,
      name: strategy.name || 'Suggested downsizing strategy',
      displayName: card.words.title,
      type: 'downsizer',
      source: 'suggestion_engine',
      // The client's figures it was priced on, so Home can tell when they change (chosenPlanRefresh.ts).
      inputsFingerprint: profileFingerprint(factFind),
      inputs: {
        engineStrategyId: strategy.id ?? null,
        card,
        homeKey: currentHomeKey,
        legacyYears: localLegacyYears,
        planContext: { setup: { ...dzSetup }, downsizeDate: effectiveDownsizeDate || null },
        details: { ...(strategy.inputs || {}) },
        downsizeDate: effectiveDownsizeDate || null,
        setup: { ...dzSetup },
        source: 'suggestion_engine',
        seStrategyName: strategy.name ?? null,
        recommendedOptimalStrategies: recommended,
        clientSelectedIsAlternative: recommended.length > 0 && !recommended.some((r) => r.name === strategy.name),
      },
      outputs: {
        agePension: card.plan.pension,
        cashFlow: card.plan.cashflow,
        legacyEstimate: card.plan.legacy,
        netWorthSchedule: buildNetWorthSchedule((strategy.projections || []) as never),
      },
      createdAt: new Date().toISOString(),
    } as never) as never);
  };

  /** The hand-built plan as a tested plan would be read — its words, its risks, its document. */
  const manualAsStrategy = (): DzStrategyView => ({
    id: 'dz-manual',
    name: dzPlanWords({ id: 'dz-manual', inputs: details as Record<string, unknown> }, factFind).title,
    inputs: details,
    projections,
    snapshot: { legacyValue: manualLegacy ?? 0, pension: n(after?.socialSecurity), netCashflow: n(projections[0]?.netCashflow ?? after?.cashFlow) },
    deploymentBreakdown: [],
  } as unknown as DzStrategyView);
  /** Staying put, as the engine's benchmark row reads, from the screen's own run (no engine needed). */
  const stayPutAsStrategy = (): DzStrategyView => ({
    id: 'dz-stay-put', name: 'Stay in your current home', inputs: {}, projections: stayPutProjections,
    snapshot: { legacyValue: manualBaselineLegacy ?? 0, pension: n(todayFinancials?.socialSecurity), netCashflow: n(todayFinancials?.cashFlow) },
  } as unknown as DzStrategyView);
  const chooseManualPlan = () => {
    if (!setSavedStrategies) return;
    const s = manualAsStrategy();
    const move = dzMoveOfInputs(details as Record<string, unknown>, { homeName, date: effectiveDownsizeDate });
    const card: DzPlanCardRecord = {
      plan: { legacy: n(manualLegacy), pension: n(after?.socialSecurity), cashflow: n(projections[0]?.netCashflow ?? after?.cashFlow) },
      baseline: todayFinancials ? { legacy: n(manualBaselineLegacy), pension: n(todayFinancials.socialSecurity), cashflow: n(todayFinancials.cashFlow) } : null,
      words: dzPlanWords(s, factFind),
      move,
      horizonYears: localLegacyYears,
      riskKinds: [...dzRiskKindsOf('dz-manual', details as Record<string, unknown>), ...(debtPayoffPlan ? (['clearDebt'] as RiskKind[]) : [])],
      origin: 'manual',
      family: familyRecordOf(details, projections, null),
    };
    setSavedStrategies((prev) => withChosenDzPlan(prev as never, {
      id: `dz-manual-${Date.now()}`,
      name: card.words.title,
      displayName: card.words.title,
      type: 'downsizer',
      source: 'manual',
      inputsFingerprint: profileFingerprint(factFind),
      // Built by the client from a tested plan ("Customise this plan", 25 Oct 2026).
      ...(manualStartedFrom ? { customisedFrom: manualStartedFrom } : {}),
      inputs: {
        card,
        homeKey: currentHomeKey,
        legacyYears: localLegacyYears,
        details: { ...details },
        downsizeDate: effectiveDownsizeDate || null,
        debtPayoff: debtPayoffPlan || null,
        source: 'manual',
        seStrategyName: null,
      },
      outputs: { agePension: card.plan.pension, cashFlow: card.plan.cashflow, legacyEstimate: card.plan.legacy, netWorthSchedule: buildNetWorthSchedule((projections || []) as never) },
      createdAt: new Date().toISOString(),
    } as never) as never);
    setManualChosen(true);
  };

  /**
   * The Statement of Advice for a plan (dzSoa.ts) — opened, downloaded, registered. On the app's calendar-year
   * setting the plan and the benchmark are re-cut on calendar years first, so the document's rows match.
   */
  const generateDzSoa = async (strategy: DzStrategyView, field: DzStrategyView[] = dzStrategies) => {
    const periodMode = readPeriodMode();
    let picked = strategy;
    let all = field;
    const rerun = dzSuggestions?.rerun;
    if (periodMode === 'calendar_year' && rerun && strategy.id !== 'dz-manual') {
      const siblingId = dzInvestSiblingOf(strategy.id, field)?.id ?? null;
      const wanted = (rerun.candidates as { id?: unknown }[]).filter((c) => c?.id === strategy.id || c?.id === 'dz-stay-put' || (siblingId != null && c?.id === siblingId));
      if (wanted.length) {
        try {
          const rows = (await calculationApi.evaluateDownsizerCandidates(factFind, wanted, rerun.legacyYears, rerun.date, 'calendar_year')) as Array<{ id?: string; projections?: ProjectionYear[] }>;
          const byId = new Map(rows.map((r) => [String(r.id), r.projections || []]));
          picked = { ...strategy, projections: byId.get(String(strategy.id)) ?? strategy.projections };
          all = field.map((s) => (byId.has(String(s.id)) ? { ...s, projections: byId.get(String(s.id)) } : s));
        } catch { /* the financial-year rows stand */ }
      }
    }
    const snapshot = buildDownsizerSoaSnapshot({ strategy: picked, all, factFind, legacyYears: localLegacyYears, setup: dzSetup, periodMode, annuityQuote: dzAnnuityQuote, homeName, customisedFrom: strategy.id === 'dz-manual' ? manualStartedFrom : null });
    openModuleSoa(snapshot);
    onSoaGenerated?.({ id: strategy.id, name: strategy.name, type: 'downsizer', source: strategy.id === 'dz-manual' ? 'manual' : 'suggestion_engine', strategy, soaSnapshot: snapshot });
  };
  const generateManualSoa = () => generateDzSoa(manualAsStrategy(), [stayPutAsStrategy(), manualAsStrategy()]);

  // Explain this / What could go wrong — for a tested plan, the hand-built plan, or the chosen plan.
  const [explainSeId, setExplainSeId] = useState<string | null>(null);
  const [explainManual, setExplainManual] = useState(false);
  const [riskTarget, setRiskTarget] = useState<{ title: string; kinds: RiskKind[]; inputs: Record<string, unknown> | null; horizonWarning: string | null } | null>(null);
  const explainStrategy: DzStrategyView | null = useMemo(() => (explainSeId ? (dzStrategies.find((s) => s.id === explainSeId) || null) : null), [dzStrategies, explainSeId]);
  const [explainView, setExplainView] = useState<SeView | null>(null);
  useEffect(() => {
    if (!explainStrategy) { setExplainView(null); return undefined; }
    let cancelled = false;
    const cfg: DownsizeDetails = explainStrategy.inputs || {};
    getDownsizeBeforeAfter(factFind, cfg).catch(() => ({ before: {} as Financials, after: {} as Financials })).then(({ before: sBefore, after: sAfter }) => {
      if (cancelled) return;
      const proj = explainStrategy.projections || [];
      const m = dzMoveOfInputs(cfg as Record<string, unknown>, { rentPerWeek: explainStrategy.rentPerWeek });
      const sLegacy = proj.length ? (estateOf(proj[localLegacyYears - 1] ?? proj[proj.length - 1]) ?? sAfter?.netWorth ?? 0) : (sAfter?.netWorth ?? 0);
      setExplainView({
        id: explainStrategy.id, name: explainStrategy.name, before: sBefore, after: sAfter, projections: proj,
        hasDownsize: m.salePrice > 0, salePrice: m.salePrice, sellingCosts: m.sellingCosts, newHomePrice: m.newHomePrice, stampDuty: m.stampDuty, freedCapital: m.freedCapital,
        buyUpShortfall: m.buyUpShortfall, sellAndRent: m.sellAndRent, rentPerWeek: m.rentPerWeek,
        downsizerContribution: n(cfg.downsizerContribution) + n(cfg.partnerDownsizerContribution),
        legacyEstimate: sLegacy,
        insightFinancials: { agePensionBefore: sBefore?.socialSecurity ?? 0, agePensionAfter: sAfter?.socialSecurity ?? 0, cashflowAfter: proj[0]?.netCashflow ?? proj[0]?.cashFlow ?? sAfter?.cashFlow ?? 0, legacyEstimate: sLegacy },
      });
    });
    return () => { cancelled = true; };
  }, [explainStrategy, factFind, localLegacyYears]);
  const explainViewReady = !!explainStrategy && !!explainView && explainView.id === explainStrategy.id;
  const manualModel = useMemo<SeView>(() => {
    const m = dzMoveOfInputs(details as Record<string, unknown>);
    return {
      before, after, projections, hasDownsize, salePrice: m.salePrice, sellingCosts: m.sellingCosts, newHomePrice: m.newHomePrice, stampDuty: m.stampDuty, freedCapital: m.freedCapital, buyUpShortfall: m.buyUpShortfall,
      sellAndRent: m.sellAndRent, rentPerWeek: m.rentPerWeek, downsizerContribution: n(details.downsizerContribution) + n(details.partnerDownsizerContribution), legacyEstimate: n(manualLegacy),
      insightFinancials: { agePensionBefore: n(before?.socialSecurity), agePensionAfter: n(after?.socialSecurity), cashflowAfter: n(projections[0]?.netCashflow ?? after?.cashFlow), legacyEstimate: n(manualLegacy) },
    };
  }, [before, after, projections, hasDownsize, details, manualLegacy]);

  const openRisksFor = (row: DzStrategyView) => setRiskTarget({ title: dzPlanWords(row, factFind).title, kinds: dzRiskKindsOf(String(row.id || ''), row.inputs as Record<string, unknown>), inputs: row.inputs as Record<string, unknown>, horizonWarning: dzAnnuityHorizonWarningOf(row, factFind) });

  /*
   * THE DEEP DIVE'S YEAR TOGGLE: re-cutting a tested plan on the calendar year runs it again through the engine,
   * with the candidate, horizon and date from the same builder that ranked it.
   */
  const [seDeepDive, setSeDeepDive] = useState<{ projections: ProjectionYear[]; name?: string; id?: string } | null>(null);
  const [showStayPutProjections, setShowStayPutProjections] = useState(false);
  const [modalPeriodMode, setModalPeriodMode] = useState<ProjectionsModalPeriodMode>(readPeriodMode);
  const [calendarYearProjections, setCalendarYearProjections] = useState<ProjectionYear[] | null>(null);
  const deepDiveCandidate = useMemo(() => {
    const rerun = dzSuggestions?.rerun;
    if (!rerun || !seDeepDive?.id) return null;
    const candidate = (rerun.candidates as { id?: unknown }[]).find((c) => c?.id === seDeepDive.id);
    return candidate ? { candidate, legacyYears: rerun.legacyYears, date: rerun.date } : null;
  }, [dzSuggestions, seDeepDive]);
  useEffect(() => {
    if (modalPeriodMode !== 'calendar_year' || !seDeepDive || !deepDiveCandidate) { setCalendarYearProjections(null); return undefined; }
    let cancelled = false;
    setCalendarYearProjections(null);
    calculationApi.evaluateDownsizerCandidates(
      factFind,
      [deepDiveCandidate.candidate],
      deepDiveCandidate.legacyYears,
      deepDiveCandidate.date,
      'calendar_year',
    ).then((rows: unknown) => {
      if (cancelled) return;
      const first = Array.isArray(rows) ? (rows[0] as { projections?: ProjectionYear[] } | undefined) : undefined;
      setCalendarYearProjections(first?.projections ?? []);
    });
    return () => { cancelled = true; };
  }, [modalPeriodMode, seDeepDive, deepDiveCandidate, factFind]);

  // "Every way we tested", worded for the reader; Escape closes it.
  const [showAllSeStrategies, setShowAllSeStrategies] = useState(false);
  /** "Compare the two homes side by side" (journey 2). */
  const [comparingHomes, setComparingHomes] = useState(false);
  const [allSortBy, setAllSortBy] = useState<'legacy' | 'cashflow' | 'pension'>('legacy');
  useEffect(() => {
    if (!showAllSeStrategies) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setShowAllSeStrategies(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showAllSeStrategies]);

  // What the plan page asked for, done once the tested plans are back.
  useEffect(() => {
    if (!pendingHubAction || seLoading || !dzSuggestions) return;
    const row = dzStrategies.find((x) => x.id === (selectedSeId ?? dzChosenEngineId));
    setPendingHubAction(null);
    if (!row) return;
    if (pendingHubAction === 'explain') setExplainSeId(row.id ?? null);
    else void generateDzSoa(row);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingHubAction, seLoading, dzSuggestions]);

  /** The chosen plan's sale and home, back on the steps (the plan page's "Change the sale" and a reopen). */
  const restoreChosenHome = () => {
    const card = dzChosen?.inputs?.card;
    if (!card) return;
    const m = card.move;
    setGoal({
      dzSalePrice: m.salePrice, dzSellingCosts: m.sellingCosts, dzDownsizeDate: m.date || '',
      dzHousing: m.sellAndRent ? 'rent' : 'buy', ...(m.sellAndRent ? { dzRentPerWeek: m.rentPerWeek } : { dzNewHomePrice: m.newHomePrice }),
      dzNewHomeState: m.state, dzHomeName: m.homeName || '',
    });
  };
  const openSteps = (to: AnalyserStep) => { setDzView('steps'); setAnotherHome(false); if (to === 3) setStrategyMode('optimised'); goToStep(to); };
  const seedManualFrom = (inputs: Record<string, unknown> | null | undefined, label: string | null) => {
    setLedger((prev) => dzLedgerFromDetails(inputs, prev));
    setManualPlanEntered(true);
    setManualStartedFrom(label);
    setManualLastChange(null);
    setManualChosen(false);
  };
  /** The chosen hand-built plan, reopened in the ledger as it was chosen (its levers, its debt plan), still chosen. */
  const reopenManual = () => {
    const ins = dzChosen?.inputs as { details?: Record<string, unknown>; debtPayoff?: DebtPayoff | null } | undefined;
    restoreChosenHome();
    // Its own record says where it began, if it was customised from a tested plan (25 Oct 2026).
    seedManualFrom(ins?.details ?? null, (dzChosen as { customisedFrom?: string } | null)?.customisedFrom ?? null);
    if (ins?.debtPayoff) { setDebtPayments(ins.debtPayoff.payments || {}); setDebtFunding((ins.debtPayoff.funding || {}) as Record<string, number>); setDebtRepaymentBasis(ins.debtPayoff.repaymentBasis || 'keep_repayment'); }
    setManualChosen(true);
    setDzView('steps'); setStrategyMode('manual'); setStep(3); scrollAppToTop();
  };
  /** The plan page asked for the hand-built plan's document: made once its figures are back. */
  const [pendingManualAdvice, setPendingManualAdvice] = useState(false);
  useEffect(() => {
    if (!pendingManualAdvice || !projections.length || !todayFinancials || !stayPutProjections.length) return;
    setPendingManualAdvice(false);
    void generateManualSoa();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingManualAdvice, projections, todayFinancials, stayPutProjections]);
  /** The "…and clear your debts" twin carries its payoff on the candidate, not on the row (25 Oct 2026). */
  const seedDebtFrom = (id: unknown) => {
    const cand = (dzSuggestions?.rerun.candidates as Array<{ id?: unknown; debtPayoff?: DebtPayoff }> | undefined)?.find((c) => c?.id === id);
    setDebtPayments({ ...(cand?.debtPayoff?.payments || {}) });
    setDebtFunding({ ...((cand?.debtPayoff?.funding || {}) as Record<string, number>) });
    setDebtRepaymentBasis(cand?.debtPayoff?.repaymentBasis === 'lower_repayment' ? 'lower_repayment' : 'keep_repayment');
  };
  /**
   * A BLANK hand-built plan (owner, 25 Oct 2026): the sale and the new home as step 1 decided them, nothing put
   * to work, no debts cleared — what "Build a plan yourself" and "Clear and start again" open. It used to open on
   * whatever the ledger last held, so a plan customised earlier reappeared under the blank door.
   */
  const clearManual = () => {
    setLedger(ledgerFromStep1());
    setManualPlanEntered(false);
    setManualStartedFrom(null);
    setManualLastChange(null);
    setManualChosen(false);
    setDebtPayments({});
    setDebtFunding({});
    setDebtRepaymentBasis('keep_repayment');
  };
  /** "Customise this plan" (a tested plan) or "Build a plan yourself" (none): the two doors into the ledger. */
  const openBuild = (from: DzStrategyView | null) => {
    clearManual();
    if (from) { seedManualFrom(from.inputs as Record<string, unknown>, dzPlanWords(from, factFind).title); seedDebtFrom(from.id); }
    setStrategyMode('manual');
    setStep(3);
    scrollAppToTop();
  };

  // ───────────────────────────── the plan page ─────────────────────────────
  const riskPanel = riskTarget && (
    <StrategyRisksPanel
      voice={voice}
      strategyName={riskTarget.title}
      onClose={() => setRiskTarget(null)}
      risks={dzPlanRisks({ kinds: riskTarget.kinds, move: riskTarget.inputs ? dzMoveOfInputs(riskTarget.inputs) : null, ipStampDuty: n((riskTarget.inputs?.deployment as { ipStampDuty?: unknown } | undefined)?.ipStampDuty) || null, horizonWarning: riskTarget.horizonWarning })}
    />
  );
  if (dzView === 'hub' && dzChosen?.inputs?.card) {
    const card = dzChosen.inputs.card;
    return (
      <div className="max-w-[1520px] mx-auto">
        <DzHub
          factFind={factFind}
          plan={dzChosen}
          outOfDate={chosenPlanOutOfDate(dzChosen, factFind)}
          homeCount={dzHomePlans.length}
          onChangeSale={() => { restoreChosenHome(); openSteps(1); }}
          onAnotherHome={() => { restoreChosenHome(); setGoal({ dzHomeName: '', dzHousing: 'buy' }); setDzView('steps'); setAnotherHome(true); setStep(1); scrollAppToTop(); }}
          // "A new model from scratch" (owner, 22 Oct 2026): what Home's "Start a new model" does, from here — the home,
          // the price and the answers taken off the file, step 1 again, and step 3's questions asked afresh.
          onStartFresh={() => {
            setFactFind?.((prev) => {
              const g = { ...(prev?.goals || {}) } as Record<string, unknown>;
              for (const k of ['dzNewHomePrice', 'dzHomeName', 'dzRentPerWeek', 'dzEmergencyReserve', 'dzDownsizeDate', 'dzSalePrice', 'dzSellingCosts']) delete g[k];
              return { ...prev, goals: g };
            });
            // Nothing hand-built carries over either: the ledger follows step 1 again until a plan is built.
            setManualPlanEntered(false); setManualStartedFrom(null); setManualLastChange(null); setManualChosen(false);
            setDzAsking(true);
            openSteps(1);
          }}
          onAllPlans={() => { restoreChosenHome(); openSteps(3); }}
          onExplain={() => {
            if (dzChosen.source === 'manual') { reopenManual(); setExplainManual(true); return; }
            restoreChosenHome(); setPendingHubAction('explain'); openSteps(3);
          }}
          // The same specifics the tested plan's panel says (22 Oct 2026): the saved plan carries its details, so the
          // guaranteed income's horizon warning is read from them and the client file, as on the plans screen.
          onRisks={() => {
            const details = (dzChosen.inputs?.details || null) as Record<string, unknown> | null;
            setRiskTarget({ title: card.words.title, kinds: card.riskKinds, inputs: details, horizonWarning: details ? dzAnnuityHorizonWarningOf({ inputs: details as DownsizeDetails }, factFind) : null });
          }}
          onAdvice={() => {
            if (dzChosen.source === 'manual') { reopenManual(); setPendingManualAdvice(true); return; }
            restoreChosenHome(); setPendingHubAction('advice'); openSteps(3);
          }}
          onBuild={() => {
            // A hand-built plan reopens with every lever it was built with, still chosen; a tested one seeds the ledger.
            if (dzChosen.source === 'manual') { reopenManual(); return; }
            restoreChosenHome();
            clearManual();
            seedManualFrom((dzChosen.inputs?.details || null) as Record<string, unknown> | null, card.words.title);
            seedDebtFrom(dzChosenEngineId);
            setDzView('steps'); setStrategyMode('manual'); setStep(3); scrollAppToTop();
          }}
          onBuildNew={() => { restoreChosenHome(); clearManual(); setDzView('steps'); setStrategyMode('manual'); setStep(3); scrollAppToTop(); }}
          onGuide={onBack}
          onAssumptions={() => setActivePage('AssumptionsPage')}
          onHome={() => setActivePage('Dashboard')}
        />
        {riskPanel}
      </div>
    );
  }

  // ───────────────────────────── the steps ─────────────────────────────
  const stepTitle = step === 1 ? (anotherHome ? 'Another home' : 'The sale and the new home') : step === 2 ? 'What it means' : strategyMode === 'manual' ? 'Build a plan yourself' : 'Ways to use the money';
  const stayPutCash = n(todayFinancials?.cashFlow);
  const ways = dzStrategies.filter((s) => !isDzBaseline(s));
  const chosenHere = !!dzChosen?.inputs?.card && dzChosen.inputs.homeKey === currentHomeKey && dzStrategies.some((x) => x.id === (selectedSeId ?? dzChosenEngineId));
  chosenHereRef.current = chosenHere;
  const notices = (() => {
    if (!dzSuggestions) return null;
    if (chosenHere && dzChosen?.inputs?.card) {
      return (
        <div className="flex flex-wrap items-center gap-4 rounded-2xl border border-slate-200 border-l-4 border-l-emerald-500 bg-white px-5 py-4 shadow-card" data-testid="dz-chosen-banner">
          <span className="text-[26px] leading-none text-emerald-500" aria-hidden>✓</span>
          <p className="min-w-0 flex-1 text-[14px] leading-relaxed text-slate-700">
            <strong className="text-slate-900">{voice.you ? "You've" : `${voice.Names} ${voice.have}`} chosen &ldquo;{dzChosen.inputs.card.words.title}&rdquo;.</strong>{' '}
            It&apos;s saved with {voice.possessive} file. The next step is the advice document, then telling Services Australia when the home is sold.
          </p>
          <button type="button" onClick={() => setDzView('hub')} className="text-[14px] font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4" data-testid="dz-go-to-plan-page">Go to the downsizing plan</button>
        </div>
      );
    }
    const chosenCard = dzChosen?.inputs?.card;
    if (chosenCard && dzChosen?.inputs?.homeKey !== currentHomeKey) {
      const best = ways.filter((s) => !isWhatIf(s)).reduce<DzStrategyView | null>((top, s) => (top == null || n(s.snapshot?.legacyValue) > n(top.snapshot?.legacyValue) ? s : top), null);
      const chosenLeaves = chosenCard.plan.legacy;
      const thisLeaves = n(best?.snapshot?.legacyValue);
      const homeWords = forHome ? `the ${dzPriceWords(namedPrice)} home` : housing === 'rent' ? 'renting' : namedPrice > 0 ? `a ${dzPriceWords(namedPrice)} home` : 'this home';
      return (
        <p className="rounded-xl border border-slate-200 border-l-4 border-l-amber-400 bg-white px-4 py-3 text-[14px] leading-relaxed text-slate-700" data-testid="dz-other-home-note">
          Re-tested for {homeWords}. {voice.Possessive} chosen plan, {chosenCard.words.title.charAt(0).toLowerCase()}{chosenCard.words.title.slice(1)},{' '}
          {best ? (chosenLeaves >= thisLeaves ? <>still leaves the family more: {formatCurrency(Math.round(chosenLeaves))} against {formatCurrency(Math.round(thisLeaves))}. </> : <>leaves the family {formatCurrency(Math.round(chosenLeaves))}, against {formatCurrency(Math.round(thisLeaves))} here. </>) : null}
          It stays chosen until {voice.they} choose here.
          {' '}<button type="button" onClick={() => setComparingHomes(true)} className="font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4" data-testid="dz-compare-homes">Compare the two homes side by side</button>
        </p>
      );
    }
    return null;
  })();

  return (
    <div className="max-w-[1520px] mx-auto">
      <p className="text-[13px] text-slate-500">
        <button type="button" onClick={() => setActivePage('Dashboard')} className="underline decoration-dotted underline-offset-2 hover:text-slate-800">Home</button>
        {' › Downsizing › '}{forHome && step === 3 ? `${forHome} › ` : ''}{stepTitle}
      </p>
      <h1 className="mt-2 text-[32px] font-bold leading-tight tracking-tight text-brand-navy" data-testid="dz-title">{voice.you ? 'Your downsizing' : `Downsizing for ${voice.names}`}</h1>
      {step === 1 && !anotherHome && <p className="mt-1 text-[15px] text-slate-600">Three short steps: the sale and the new home, what it would mean for {voice.them}, and the ways to use any money it frees.</p>}

      {!hasHome ? (
        <p className="mt-6 rounded-xl border border-slate-200 border-l-4 border-l-amber-400 bg-white px-4 py-3 text-[14px] text-slate-700">Downsizing applies when {voice.you ? 'you own' : `${voice.names} own${voice.s}`} a home. There&apos;s no home recorded in {voice.possessive} profile, so there&apos;s nothing to downsize here.</p>
      ) : (
      <>
      <AnalyserSteps
        step={step}
        onStep={(s) => { if (s !== 1) setAnotherHome(false); goToStep(s); }}
        ariaLabel="Downsizing steps"
        steps={[
          { title: 'The sale and the new home', sub: sale.salePrice > 0 ? `${formatCurrency(sale.salePrice)}${dutyState ? ` · ${dutyState}` : ''}` : 'Prices, costs, stamp duty' },
          { title: `What it means for ${voice.them}`, sub: todayFinancials ? `${stayPutCash < 0 ? `short ${formatCurrency(Math.round(-stayPutCash))}` : `${formatCurrency(Math.round(stayPutCash))} a year spare`}` : 'Staying put, in four numbers' },
          { title: 'Ways to use the money', sub: 'Plans we tested, or build your own' },
        ]}
      />

      {step === 1 && anotherHome && (
        <div className="mt-8">
          <DzAnotherHome
            factFind={factFind}
            setFactFind={setFactFind}
            salePrice={sale.salePrice}
            sellingCosts={sale.sellingCosts}
            state={dutyState}
            chosenTitle={dzChosen?.inputs?.card?.words.title ?? null}
            onRun={() => { setAnotherHome(false); setStrategyMode('optimised'); setDzAsking(false); goToStep(3); }}
            onCancel={() => { setAnotherHome(false); if (dzChosen) { restoreChosenHome(); setDzView('hub'); } }}
          />
        </div>
      )}
      {step === 1 && !anotherHome && (
        <div className="mt-8">
          <DzStep1 factFind={factFind} setFactFind={setFactFind} sale={sale} onSale={onSale} onNext={() => goToStep(2)} onReadGuide={onBack} />
        </div>
      )}

      {step === 2 && (
        <div className="mt-8">
          <DzStep2
            factFind={factFind}
            todayFinancials={todayFinancials as { socialSecurity?: number; cashFlow?: number; netWorth?: number; socialSecurityBreakdown?: unknown } | null}
            stayPutLegacy={manualBaselineLegacy}
            runway={stayPutRunway}
            legacyYears={localLegacyYears}
            salePrice={sale.salePrice}
            sellingCosts={sale.sellingCosts}
            onLegacyYearsChange={setLocalLegacyYears}
            onBack={() => goToStep(1)}
            onNext={() => { setStrategyMode('optimised'); goToStep(3); }}
            onAssumptions={() => setActivePage('AssumptionsPage')}
            onShowProjections={() => setShowStayPutProjections(true)}
          />
        </div>
      )}

      {step === 3 && strategyMode === 'optimised' && (
        <div className="mt-8">
          {dzAsking ? (
            <DzQuestions
              factFind={factFind}
              sale={sale}
              housing={housing}
              newHomePrice={namedPrice}
              state={dutyState}
              firstRun={(factFind?.goals as Record<string, unknown> | undefined)?.dzEmergencyReserve == null}
              onRun={(answers) => {
                setGoal(dzAnswersToGoals(answers));
                setNewMoneySkipped(true);
                setDzAsking(false);
                scrollAppToTop();
              }}
              onBuildYourOwn={() => { setDzAsking(false); openBuild(null); }}
              onCancel={(factFind?.goals as Record<string, unknown> | undefined)?.dzEmergencyReserve == null ? undefined : () => setDzAsking(false)}
            />
          ) : !hasDownsize ? (
            <p className="rounded-xl border border-slate-200 border-l-4 border-l-amber-400 bg-white px-4 py-3 text-[14px] text-slate-700">Enter the <strong>sale price</strong> on step 1, and we&apos;ll compare the ways to use the money.</p>
          ) : needsNewMoneyAnswer ? (
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card"><NewMoneyProfileGate onAnswer={answerNewMoneyProfile} onSkip={() => setNewMoneySkipped(true)} contextLine="The plans we'll compare put the money the sale frees to work." /></div>
          ) : needsTbcAnswer ? (
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card">
              <TransferCapGate name={(tbcPersonNeedingAnswer === 'partner' ? factFind?.partner?.firstName : factFind?.client?.firstName) || 'this person'} onAnswer={answerTbc} onSkip={() => setTbcSkipped(true)} />
            </div>
          ) : seLoading || (!dzSuggestions && engineWanted) ? (
            <SuggestionEngineAnalysisLoader title="Testing the ways to use the money" subtitle="We're modelling each way of downsizing, and staying put, over the same years." />
          ) : !dzSuggestions || dzStrategies.length === 0 ? (
            <p className="text-[14px] text-slate-600">We couldn&apos;t test the plans for this sale. <button type="button" className="font-semibold text-brand-navy underline" onClick={() => openBuild(null)}>Build a plan yourself</button> instead.</p>
          ) : (
            <DzPlansView
              notices={notices}
              factFind={factFind}
              strategies={dzStrategies.map((s) => (isWhatIf(s) ? { ...s, sensitivity: true } : s))}
              stayPut={dzStayPut}
              recommended={dzRecommended}
              setAside={dzSetAside}
              horizonYears={localLegacyYears}
              ownToday={n(todayFinancials?.netWorth)}
              focus={optimisationFocus}
              onFocus={setOptimisationFocus}
              chosenId={selectedSeId ?? (chosenHere ? dzChosenEngineId : null)}
              onChoose={selectEngineStrategy}
              onIncomeOnly={incomeOnlySetter(setFactFind)}
              onExplain={(row) => setExplainSeId(row.id ?? null)}
              onRisks={openRisksFor}
              onYearByYear={(row) => setSeDeepDive({ projections: row.projections || [], name: dzPlanWords(row, factFind).title, id: row.id })}
              renderDetail={(row) => {
                const terms = deploymentAnnuityTerms(row.inputs?.deployment as ProceedsDeploymentValue | undefined, isCouple);
                const rows = dzWhereTheMoneyGoes(row.inputs as Record<string, unknown>, row.deploymentBreakdown);
                return (
                  <div className="space-y-3 text-[13px] text-slate-700" data-testid="dz-card-detail">
                    {rows.length > 0 && (
                      <table className="w-full tabular-nums"><tbody>
                        {rows.map((r) => <tr key={r.label} className="border-b border-slate-100"><td className="py-1">{r.label}</td><td className="py-1 text-right">{formatCurrency(Math.round(r.amount))}</td></tr>)}
                      </tbody></table>
                    )}
                    {row.description && <p className="text-slate-600">{row.description}</p>}
                    {terms && (
                      <EngineAnnuityTerms
                        terms={terms}
                        factFind={factFind}
                        quote={(
                          <>
                            <AnnuityQuoteControl
                              draft={dzAnnuityQuoteDraft}
                              onDraft={setDzAnnuityQuoteDraft}
                              committed={dzAnnuityQuote?.income ?? null}
                              scope={<>Have a quote from a provider for this guaranteed income? Enter the yearly income they offered for {formatCurrency(Number(terms.purchaseAmount) || 0)}. Plans with the same type are re-compared on it, scaled to their amount; other types keep our estimate.</>}
                              onCommit={(income) => setDzAnnuityQuote(income != null && income > 0 ? { income, amount: Number(terms.purchaseAmount) || 0, shape: annuityQuoteShapeOf(terms.annuityType, terms.residualPercent) } : null)}
                            />
                            {dzAnnuityQuote && <p className="mt-2 text-[12px] text-slate-600" data-testid="dz-annuity-quote-note">{annuityQuoteNote(dzAnnuityQuote, formatCurrency)}</p>}
                          </>
                        )}
                      />
                    )}
                    <button type="button" onClick={() => setSeDeepDive({ projections: row.projections || [], name: dzPlanWords(row, factFind).title, id: row.id })} className="font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4">See the year-by-year figures</button>
                  </div>
                );
              }}
              onAllWays={() => setShowAllSeStrategies(true)}
              onChanges={() => { setDzAsking(true); scrollAppToTop(); }}
              onBuildYourOwn={openBuild}
              onAdvice={() => { const row = dzStrategies.find((x) => x.id === (selectedSeId ?? dzChosenEngineId)); if (row) void generateDzSoa(row); }}
              forHome={forHome}
              whatIfCount={dzStrategies.filter((s) => !isDzBaseline(s) && isWhatIf(s)).length}
            />
          )}
        </div>
      )}

      {step === 3 && strategyMode === 'manual' && (
        <div className="mt-8">
          <DzBuildPlan
            factFind={factFind}
            setFactFind={setFactFind}
            ledger={ledger}
            onLedger={onLedger}
            reserve={reserve}
            horizonYears={localLegacyYears}
            startedFrom={manualStartedFrom}
            onClear={clearManual}
            startOptions={[
              ...dzGoalLeaders.slice(0, 3).map((l) => ({ id: String(l.id), label: dzPlanWords(l, factFind).title })),
              { id: 'blank', label: 'A blank plan' },
            ]}
            onStartFrom={(id) => {
              if (id === 'blank') { clearManual(); return; }
              const row = dzStrategies.find((x) => String(x.id) === id);
              if (row) { clearManual(); seedManualFrom(row.inputs as Record<string, unknown>, dzPlanWords(row, factFind).title); seedDebtFrom(row.id); }
            }}
            working={dzCalcWorking}
            effect={{
              cashflow: projections.length ? n(projections[0]?.netCashflow ?? after?.cashFlow) : null,
              baseCashflow: todayFinancials ? n(todayFinancials.cashFlow) : null,
              pension: projections.length ? n(after?.socialSecurity) : null,
              basePension: todayFinancials ? n(todayFinancials.socialSecurity) : null,
              legacy: projections.length ? n(manualLegacy) : null,
              baseLegacy: manualBaselineLegacy,
            }}
            lastChange={manualLastChange}
            onExplain={() => setExplainManual(true)}
            onRisks={() => setRiskTarget({ title: dzPlanWords({ id: 'dz-manual', inputs: details as Record<string, unknown> }, factFind).title, kinds: [...dzRiskKindsOf('dz-manual', details as Record<string, unknown>), ...(debtPayoffPlan ? (['clearDebt'] as RiskKind[]) : [])], inputs: details as Record<string, unknown>, horizonWarning: dzAnnuityHorizonWarningOf({ inputs: details }, factFind) })}
            onChoose={chooseManualPlan}
            chosen={manualChosen}
            onBackToPlans={() => { setStrategyMode('optimised'); scrollAppToTop(); }}
            onYearByYear={() => setSeDeepDive({ projections, name: 'The plan you built' })}
            planRows={projections as unknown[]}
            downsizeDate={effectiveDownsizeDate}
            comparison={manualChosen && setSavedStrategies ? (
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 border-t border-slate-200 pt-2 text-[13px]" data-testid="dz-build-chosen-strip">
                <span className="text-slate-600">Saved with {voice.possessive} file.</span>
                <button type="button" onClick={() => void generateManualSoa()} className="font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4" data-testid="dz-build-advice">Get the advice document</button>
                {dzChosen && <button type="button" onClick={() => setDzView('hub')} className="font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4">Go to the downsizing plan</button>}
              </div>
            ) : null}
          >
            {payableDebtsOf(factFind as never).length > 0 && (
              <DebtPayoffStrategy
                variant="section"
                heading="3 · Debts"
                factFind={factFind as never}
                debtPayments={debtPayments}
                setDebtPayments={(v) => { setDebtPayments(v as never); setManualChosen(false); }}
                debtFunding={debtFunding}
                setDebtFunding={setDebtFunding}
                repaymentBasis={debtRepaymentBasis}
                setRepaymentBasis={setDebtRepaymentBasis}
                competingUseNote="The money comes from the same sale proceeds and savings the plan puts to work, so what is spent here reduces what is left to place."
              />
            )}
          </DzBuildPlan>
        </div>
      )}
      </>
      )}

      {/* Every way tested, worded for the reader, against staying put; the price what-ifs said as what they are. */}
      {showAllSeStrategies && dzSuggestions && (() => {
        const stay = dzStayPut;
        const pick = (s: DzStrategyView) => (allSortBy === 'cashflow' ? n(s.snapshot?.netCashflow) : allSortBy === 'pension' ? n(s.snapshot?.pension) : n(s.snapshot?.legacyValue));
        const plans = ways.filter((s) => !isWhatIf(s)).sort((a, b) => pick(b) - pick(a));
        const whatIfs = ways.filter((s) => isWhatIf(s)).sort((a, b) => pick(b) - pick(a));
        const signed = (d: number) => (Math.round(d) === 0 ? null : <span className={`block text-[12px] ${d < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>{d < 0 ? '−' : '+'}{formatCurrency(Math.abs(Math.round(d)))}</span>);
        const row = (s: DzStrategyView) => {
          const isSelected = s.id === (selectedSeId ?? (chosenHere ? dzChosenEngineId : null));
          const w = dzPlanWords(s, factFind);
          const cashLeft = n(s.snapshot?.netCashflow);
          const gi = dzGuaranteedIncomeOf(s.inputs?.deployment as ProceedsDeploymentValue | undefined, factFind);
          const setter = incomeOnlySetter(setFactFind);
          return (
            <tr key={s.id || s.name} className={`border-b border-slate-100 ${isSelected ? 'bg-blue-50/60' : ''}`}>
              <td className="px-5 py-3">
                <p className="font-semibold text-slate-900">{w.title}</p>
                <p className="mt-0.5 max-w-xl text-[12.5px] text-slate-500">{w.sub}</p>
                {gi?.incomeOnly && setter && !isSelected && <div className="mt-2 max-w-xl"><IncomeOnlyBeforeChoose factFind={factFind} amount={gi.amount} onChange={setter} /></div>}
              </td>
              <td className="px-5 py-3 text-right font-semibold">{formatCurrency(n(s.snapshot?.legacyValue))}{stay && signed(n(s.snapshot?.legacyValue) - n(stay.snapshot?.legacyValue))}</td>
              <td className="px-5 py-3 text-right">{formatCurrency(n(s.snapshot?.pension))}{stay && signed(n(s.snapshot?.pension) - n(stay.snapshot?.pension))}</td>
              <td className={`px-5 py-3 text-right ${cashLeft < 0 ? 'text-rose-700' : ''}`}>{cashLeft < 0 ? `short ${formatCurrency(-cashLeft)}` : formatCurrency(cashLeft)}</td>
              <td className="px-5 py-3 text-right">{formatCurrency(dzMoveOfInputs(s.inputs as Record<string, unknown>).freedCapital)}</td>
              <td className="whitespace-nowrap px-5 py-3 text-right">
                <div className="inline-flex gap-2">
                  <Button variant={isSelected ? 'secondary' : 'brand'} size="sm" disabled={!isSelected && (dzChoiceHeld(s, factFind) || (!!gi?.incomeOnly && !!setter && incomeOnlyHeld(factFind, true, gi.amount)))} onClick={() => { selectEngineStrategy(s); setShowAllSeStrategies(false); }}>{isSelected ? 'Chosen' : 'Choose this plan'}</Button>
                  <Button variant="secondary" size="sm" onClick={() => { setSeDeepDive({ projections: s.projections || [], name: w.title, id: s.id }); setShowAllSeStrategies(false); }}>Year by year</Button>
                </div>
              </td>
            </tr>
          );
        };
        return (
          <DialogShell titleId="dz-all-ways-title" title={`Every way we tested, ${ways.length}`} eyebrow={`Against staying put, over about ${localLegacyYears} years`} onClose={() => setShowAllSeStrategies(false)} size="xl" testId="dz-all-ways-dialog" flush>
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-3">
              <p className="text-[14px] text-slate-700">These are all the ways of downsizing and using the money we worked through for {voice.them}. The plans {voice.you ? 'you were shown' : `${voice.names} ${voice.are === 'is' ? 'was' : 'were'} shown`} are the ones that do best on {voice.their} goals.</p>
              <label className="flex items-center gap-2 text-[13px] text-slate-600">Order by
                <select value={allSortBy} onChange={(e) => setAllSortBy(e.target.value as 'legacy' | 'cashflow' | 'pension')} className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-[13px]">
                  <option value="legacy">Left to the family</option>
                  <option value="cashflow">Left over each year</option>
                  <option value="pension">Age Pension</option>
                </select>
              </label>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-[14px]">
                <thead><tr className="border-b border-slate-200 text-left text-[12px] font-semibold text-slate-500">
                  <th className="px-5 py-3">The plan</th><th className="px-5 py-3 text-right">Left to the family</th><th className="px-5 py-3 text-right">Age Pension, a year</th><th className="px-5 py-3 text-right">Left over, first year</th><th className="px-5 py-3 text-right">Freed up</th><th className="px-5 py-3"></th>
                </tr></thead>
                <tbody className="tabular-nums">
                  {stay && (
                    <tr className="border-b border-slate-100 bg-slate-50">
                      <td className="px-5 py-3"><p className="font-semibold text-slate-900">Staying put</p><p className="mt-0.5 text-[12.5px] text-slate-500">What every plan is measured against.</p></td>
                      <td className="px-5 py-3 text-right">{formatCurrency(n(stay.snapshot?.legacyValue))}</td>
                      <td className="px-5 py-3 text-right">{formatCurrency(n(stay.snapshot?.pension))}</td>
                      <td className="px-5 py-3 text-right">{n(stay.snapshot?.netCashflow) < 0 ? `short ${formatCurrency(-n(stay.snapshot?.netCashflow))}` : formatCurrency(n(stay.snapshot?.netCashflow))}</td>
                      <td className="px-5 py-3 text-right">—</td><td className="px-5 py-3"></td>
                    </tr>
                  )}
                  {plans.map(row)}
                  {whatIfs.length > 0 && (
                    <tr><td colSpan={6} className="bg-white px-5 pb-1 pt-5"><p className="text-[13px] font-bold text-brand-navy" data-testid="dz-what-ifs">What if the new home cost more, or less?</p><p className="text-[12.5px] text-slate-500">The same sale with only the price changed{forHome ? `, beside ${forHome}` : ''}. Shown so the price decision can be seen on its own; {forHome ? 'they are for other homes' : namedPrice > 0 ? `the plans above all buy at the ${formatCurrency(namedPrice)} named` : 'never recommended for the reason given for moving'}.</p></td></tr>
                  )}
                  {whatIfs.map(row)}
                </tbody>
              </table>
            </div>
          </DialogShell>
        );
      })()}

      {/* Two homes side by side (journey 2): the chosen plan's home and the best plan for this one. */}
      {comparingHomes && dzChosen?.inputs?.card && (() => {
        const c = dzChosen.inputs.card;
        const best = ways.filter((s) => !isWhatIf(s)).reduce<DzStrategyView | null>((top, s) => (top == null || n(s.snapshot?.legacyValue) > n(top.snapshot?.legacyValue) ? s : top), null);
        const bm = best ? dzMoveOfInputs(best.inputs as Record<string, unknown>) : null;
        const stay = dzStayPut;
        const money = (x: number) => formatCurrency(Math.round(x));
        return (
          <DialogShell titleId="dz-homes-title" title="Two homes, side by side" eyebrow="Same sale, same years, same state" onClose={() => setComparingHomes(false)} size="xl" testId="dz-compare-homes-dialog" flush>
            <div className="overflow-x-auto">
              <table className="min-w-full text-[14px] tabular-nums">
                <thead><tr className="border-b border-slate-200 text-left text-[12px] font-semibold text-slate-500"><th className="px-5 py-3">Plan · home</th><th className="px-5 py-3 text-right">Left to the family</th><th className="px-5 py-3 text-right">Each year</th><th className="px-5 py-3 text-right">Age Pension</th><th className="px-5 py-3 text-right">Stamp duty</th><th className="px-5 py-3 text-right">Freed up</th></tr></thead>
                <tbody>
                  {stay && <tr className="border-b border-slate-100 bg-slate-50"><td className="px-5 py-3 font-semibold">Staying put<br /><span className="text-[12.5px] font-normal text-slate-500">the comparison</span></td><td className="px-5 py-3 text-right">{money(n(stay.snapshot?.legacyValue))}</td><td className="px-5 py-3 text-right">{money(n(stay.snapshot?.netCashflow))}</td><td className="px-5 py-3 text-right">{money(n(stay.snapshot?.pension))}</td><td className="px-5 py-3 text-right">—</td><td className="px-5 py-3 text-right">—</td></tr>}
                  <tr className="border-b border-slate-100"><td className="px-5 py-3 font-semibold">{c.words.title}<br /><span className="text-[12.5px] font-normal text-slate-500">{c.move.sellAndRent ? 'renting' : `about ${dzPriceWords(c.move.newHomePrice)}`}{c.move.homeName ? `, ${c.move.homeName}` : ''} · Chosen</span></td><td className="px-5 py-3 text-right">{money(c.plan.legacy)}</td><td className="px-5 py-3 text-right">{money(c.plan.cashflow)}</td><td className="px-5 py-3 text-right">{money(c.plan.pension)}</td><td className="px-5 py-3 text-right">{money(c.move.stampDuty)}</td><td className="px-5 py-3 text-right">{money(c.move.freedCapital)}</td></tr>
                  {best && bm && <tr><td className="px-5 py-3 font-semibold">{dzPlanWords(best, factFind).title}<br /><span className="text-[12.5px] font-normal text-slate-500">{bm.sellAndRent ? 'renting' : dzPriceWords(bm.newHomePrice)}{forHome ? `, ${forHome}` : ''}</span></td><td className="px-5 py-3 text-right">{money(n(best.snapshot?.legacyValue))}</td><td className="px-5 py-3 text-right">{money(n(best.snapshot?.netCashflow))}</td><td className="px-5 py-3 text-right">{money(n(best.snapshot?.pension))}</td><td className="px-5 py-3 text-right">{money(bm.stampDuty)}</td><td className="px-5 py-3 text-right">{money(bm.freedCapital)}</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap justify-end gap-2 px-5 py-3">
              {best && <Button variant="brand" size="sm" disabled={dzChoiceHeld(best, factFind)} onClick={() => { selectEngineStrategy(best); setComparingHomes(false); }}>Choose the other plan instead</Button>}
              <Button variant="secondary" size="sm" onClick={() => setComparingHomes(false)}>Back</Button>
            </div>
          </DialogShell>
        );
      })()}

      {/* Explain this — aged care's ledger (the money story inside "Put together"), then the guaranteed income, the whys and the pieces. */}
      {(explainStrategy || explainManual) && (() => {
        const isManual = !explainStrategy;
        const row = explainStrategy ?? manualAsStrategy();
        const rows = ((row.projections) || []) as ProjectionYear[];
        const base = explainStrategy ? dzStayPut?.snapshot : null;
        const baseline = isManual
          ? (todayFinancials ? { legacy: manualBaselineLegacy, pension: n(todayFinancials.socialSecurity), cashflow: n(todayFinancials.cashFlow) } : null)
          : base ? { legacy: n(base.legacyValue), pension: n(base.pension), cashflow: n(base.netCashflow) } : null;
        const gi = dzGuaranteedIncomeOf(row.inputs?.deployment as ProceedsDeploymentValue | undefined, factFind, rows);
        const sibling = gi && !isManual ? dzInvestSiblingOf(row.id, dzStrategies) : null;
        const story = summariseFunding(rows as never).hasDeficit
          ? buildMoneyStory({ rows: rows as never, factFind, runway: planRunwayCopy(rows as never, rows.length), deposit: { paid: 0, refundTo: '' }, horizonYears: localLegacyYears, enteringName: factFind?.client?.firstName || 'The household', spentOn: 'the new home' })
          : null;
        const close = () => { setExplainSeId(null); setExplainManual(false); };
        return (
          // Aged care's Explain this (owner, 22 Oct 2026: "all modules use consistent UI"): the "Explain this" chip,
          // the plan's own words as the title, one line saying how to read it, and the ledger below.
          <PlanExplanationModal
            eyebrow="Explain this"
            title={dzPlanWords(row, factFind).title}
            intro={dzExplainIntro(factFind)}
            onClose={close}
            footer={<Button variant="secondary" size="sm" onClick={() => { close(); setSeDeepDive({ projections: rows, name: dzPlanWords(row, factFind).title, id: isManual ? undefined : row.id }); }}>See the year-by-year figures</Button>}
          >
            {(!isManual && !explainViewReady) || (isManual && !projections.length) ? (
              <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-6 text-center text-[14px] text-slate-600" role="status" aria-live="polite">Working out this plan&apos;s figures…</div>
            ) : (
                <DzExplainSteps
                  factFind={factFind}
                  inputs={(row.inputs || {}) as Record<string, unknown>}
                  rentPerWeek={row.rentPerWeek}
                  plan={{ legacy: isManual ? manualLegacy : n(row.snapshot?.legacyValue), pension: n(row.snapshot?.pension), cashflow: n(row.snapshot?.netCashflow) }}
                  baseline={baseline}
                  horizonYears={localLegacyYears}
                  // The money story is told inside the "Put together" line, as aged care tells it (22 Oct 2026).
                  moneyStory={story ? <MoneyStoryBlock story={story} testId="dz-money-story" /> : null}
                  leverKinds={isManual
                    ? [...dzRiskKindsOf('dz-manual', (row.inputs || {}) as Record<string, unknown>), ...(debtPayoffPlan ? ['clearDebt'] : [])]
                    : dzRiskKindsOf(String(row.id || ''), (row.inputs || {}) as Record<string, unknown>)}
                >
                {gi && (
                  <div className="mt-5" data-testid="dz-recap-family-panel">
                    <AnnuityFamilyPanel factFind={factFind} personName={gi.personName} amount={gi.amount} kind={gi.kind} incomeAYear={gi.incomeAYear} rows={rows as never} compareRows={(sibling?.projections ?? null) as never} compareNote={sibling ? DZ_COMPARE_NOTE : null} horizonYears={localLegacyYears} testId="dz-recap-annuity-family-panel" />
                  </div>
                )}
                <DownsizerPlanRecap
                  hideSummary
                  eyebrow="Why each number moves"
                  model={isManual ? manualModel : explainView!}
                  giftingAmount={n(row.inputs?.giftingAmount)}
                  baselineLegacy={isManual ? manualBaselineLegacy : (base ? n(base.legacyValue) : manualBaselineLegacy)}
                  legacyYears={localLegacyYears}
                  factFind={factFind}
                />
                {(() => {
                  // The debts this plan keeps, and what the same plan with them paid off did ("<id>-clear-debt").
                  const twin = isManual ? null : (dzStrategies || []).find((s) => s.id === `${row.id}-clear-debt`) ?? null;
                  const cleared = isManual
                    ? Object.values((debtPayoffPlan?.payments || {}) as Record<string, unknown>).reduce<number>((t, x) => t + (Number(x) || 0), 0)
                    : /-clear-debt$/.test(String(row.id)) ? clearableDebtTotal(factFind) : 0;
                  return (
                    <DebtsInPlanNote
                      factFind={factFind}
                      clearedAmount={cleared}
                      plan={{ estate: isManual ? n(manualLegacy) : n(row.snapshot?.legacyValue), pension: n(row.snapshot?.pension), cashflow: n(row.snapshot?.netCashflow) }}
                      twin={twin ? { estate: n(twin.snapshot?.legacyValue), pension: n(twin.snapshot?.pension), cashflow: n(twin.snapshot?.netCashflow) } : null}
                      years={localLegacyYears}
                      context={isManual ? 'manual' : 'tested'}
                    />
                  );
                })()}
                {/* Money the plan invests: the mix, and how it is usually held (23 Oct 2026). */}
                {plansInvest(dzPlanWords(row, factFind).sub) && <HowItIsInvestedNote factFind={factFind} />}
                </DzExplainSteps>
            )}
          </PlanExplanationModal>
        );
      })()}

      {riskPanel}

      {/* Staying put, year by year — from step 2. */}
      {showStayPutProjections && (
        <ProjectionsModal onClose={() => setShowStayPutProjections(false)} title="Staying put — nothing changed" comparedOverYears={Number(localLegacyYears) || undefined} projections={stayPutProjections as never} factFind={factFind as never} initialFinancials={todayFinancials as never} costLabel={null} />
      )}
      {seDeepDive && (
        <ProjectionsModal
          onClose={() => setSeDeepDive(null)}
          title={seDeepDive.name}
          comparedOverYears={Number(localLegacyYears) || undefined}
          projections={(modalPeriodMode === 'calendar_year' && calendarYearProjections ? calendarYearProjections : seDeepDive.projections) as never}
          {...(deepDiveCandidate ? { onPeriodModeChange: setModalPeriodMode, isReloading: modalPeriodMode === 'calendar_year' && !calendarYearProjections } : {})}
          factFind={factFind as never}
          initialFinancials={todayFinancials as never}
          costLabel={null}
        />
      )}
    </div>
  );
};

interface DownsizerPageProps {
  setActivePage: (page: string) => void;
  factFind: DraftFactFind;
  /** The file's saved plans (App owns them): the chosen downsizing plan opens the plan page. */
  savedStrategies?: Array<Record<string, unknown>>;
  setSavedStrategies?: React.Dispatch<React.SetStateAction<Array<Record<string, unknown>>>>;
  /** Persists the answers to the client record (App owns the fact-find state). */
  setFactFind?: React.Dispatch<React.SetStateAction<DraftFactFind>>;
  /**
   * Called with each generated Statement of Advice (the strategy plus its document snapshot) so it
   * lands on the server's advice register like aged care's (owner, 2 Oct 2026). App.tsx decides where.
   */
  onSoaGenerated?: (document: Record<string, unknown>) => void;
}

const DownsizerPage = ({ setActivePage, factFind, savedStrategies, setSavedStrategies, setFactFind, onSoaGenerated }: DownsizerPageProps) => {
  // The steps first; the guide is a link from step 1's primer and the plan page (as the village, 21 Oct 2026).
  const [view, setView] = useState<'explanation' | 'calculator'>('calculator');
  // The calculator stays mounted (hidden) under the guide, and closing it puts the reader back where they were.
  const scrollWas = useRef(0);
  const scroller = () => document.querySelector('[data-app-scroll-container]') as HTMLElement | null;
  const openGuide = () => { scrollWas.current = scroller()?.scrollTop ?? window.scrollY; setView('explanation'); scrollAppToTop(); };
  const closeGuide = () => {
    setView('calculator');
    const y = scrollWas.current;
    requestAnimationFrame(() => { const el = scroller(); if (el) el.scrollTop = y; else window.scrollTo(0, y); });
  };
  return (
    <>
      {view === 'explanation' && <DownsizerExplained setActivePage={setActivePage} onContinue={closeGuide} backLabel="Back to where you were" />}
      <div className={view === 'explanation' ? 'hidden' : undefined} aria-hidden={view === 'explanation' || undefined}>
        <DownsizerAnalyser setActivePage={setActivePage} factFind={factFind} onBack={openGuide} savedStrategies={savedStrategies} setSavedStrategies={setSavedStrategies} setFactFind={setFactFind} onSoaGenerated={onSoaGenerated} />
      </div>
    </>
  );
};

export default DownsizerPage;
